import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import PokerVisual from '../components/PokerVisual/PokerVisual';
import { ActionType, PlaygroundPosition, Street } from '../pages/PlaygroundPage/PlaygroundPage';
import CoachingBubble from '../components/CoachingBubble/CoachingBubble';
import ReplayerActionTimeline from '../components/ActionTimeline/ReplayerActionTimeline';
import BetAmountInput from '../components/BetAmountInput/BetAmountInput';
import SetupPopup from '../components/SetupPopup/SetupPopup';
import * as Tabs from "@radix-ui/react-tabs";
import * as Dialog from "@radix-ui/react-dialog";
import './HandReplayerPage.css';
import CardPickerDialog from '../components/CardPickerDialog/CardPickerDialog';
import { getPositionLabel, getSeatIdFromPosition, createInitialAutoFoldIds, getInitialStacks } from '../utils/seatUtils';
import { calculatePotSize, calculateStackSizes, calculateStreetContributions, calculateFoldedPlayers, getCommunityCards, calculateCurrentBet, capBetAmount } from '../utils/pokerCalculations';
import { 
  CardPosition, 
  HeroCards, 
  CommunityCards, 
  getUsedCards as getUsedCardsUtil, 
  shouldAutoTransition, 
  updateHeroCards, 
  updateCommunityCards, 
  isHeroCardPosition,
  isCommunityCardPosition 
} from '../utils/cardUtils';
import { useAutoPlay } from '../hooks/useAutoPlay';
import { PlayFab } from '../app/services/PlayFab';

// Define the structure of the hand summary JSON based on your example
export interface HandSummaryStreetAction {
  playerId: number;
  type: string; // Will be mapped to ActionType
  amount: number | null;
}

interface HandSummaryStreet {
  potSizeAtStart: number;
  potSizeAtEnd: number;
  cardsDealt: string[] | null;
  actions: HandSummaryStreetAction[];
}

interface HandSummaryMetadata {
  handId: string;
  timestamp: string;
  handType: string;
  tableType: string;
  stakes: {
    smallBlind: number;
    bigBlind: number;
  };
}

interface HandSummaryPlayer {
  id: number;
  name: string;
  isHero: boolean;
  position: PlaygroundPosition; // Assuming this maps directly
  initialStack: number;
}

interface HandSummaryHeroCards {
  holeCard1: string;
  holeCard2: string;
}

export interface HandSummaryFull {
  metadata: HandSummaryMetadata;
  players: HandSummaryPlayer[];
  heroCards: HandSummaryHeroCards;
  board: string[] | null; // Combined board cards
  preflop: HandSummaryStreet;
  flop: HandSummaryStreet;
  turn: HandSummaryStreet;
  river: HandSummaryStreet;
  finalPot: number;
  heroTotalContribution: number;
  declaredWinnerIds: number[] | null;
  isChoppedPot: boolean | null;
  analysisJson?: any | null; // Made analysisJson optional here for the initial data
}

// Define SeatMeta based on PokerVisual's needs
interface SeatMeta {
  id: number;
  label: PlaygroundPosition;
  tag?: string;
}

// The original simple hand summary data
const handSummaryData: HandSummaryFull = {
  "metadata": {
    "handId": "masm3pnwh5rjki",
    "timestamp": "2024-06-15T12:00:00Z",
    "handType": "Live",
    "tableType": "Cash Game",
    "stakes": { "smallBlind": 2, "bigBlind": 2 }
  },
  "players": [
    { "id": 0, "name": "Hero", "isHero": true, "position": "MP", "initialStack": 200 },
    { "id": 1, "name": "Player 1", "isHero": false, "position": "BTN", "initialStack": 200 }
  ],
  "heroCards": { "holeCard1": "Qd", "holeCard2": "Th" },
  "board": ["Ac", "Ad", "Jd", "9d", "Td"], // Full board for reference
  "preflop": {
    "potSizeAtStart": 4, 
    "potSizeAtEnd": 14,
    "cardsDealt": null,
    "actions": [{ "playerId": 0, "type": "Raise", "amount": 6 }]
  },
  "flop": {
    "potSizeAtStart": 14,
    "potSizeAtEnd": 120, 
    "cardsDealt": ["Ac", "Ad", "Jd"],
    "actions": [{ "playerId": 0, "type": "Bet", "amount": 20 }]
  },
  "turn": {
    "potSizeAtStart": 120, 
    "potSizeAtEnd": 220,   
    "cardsDealt": ["9d"],
    "actions": [
      { "playerId": 1, "type": "Bet", "amount": 100 },
      { "playerId": 0, "type": "Call", "amount": 100 }
    ]
  },
  "river": {
    "potSizeAtStart": 220, 
    "potSizeAtEnd": 220,   
    "cardsDealt": ["Td"],
    "actions": []
  },
  "finalPot": 220, 
  "heroTotalContribution": 126,
  "declaredWinnerIds": null,
  "isChoppedPot": null,
  "analysisJson": null 
};

// New Detailed Hand Summary Data
const detailedHandSummaryData: HandSummaryFull = {
  "metadata": {
    "handId": "detailXYZ789",
    "timestamp": "2024-07-27T10:00:00Z",
    "handType": "Online Tournament",
    "tableType": "6-Max",
    "stakes": { "smallBlind": 50, "bigBlind": 100 }
  },
  "players": [
    { "id": 3, "name": "PlayerUTG", "isHero": false, "position": "UTG", "initialStack": 8000 },
    { "id": 4, "name": "PlayerMP", "isHero": false, "position": "MP", "initialStack": 11000 },
    { "id": 5, "name": "PlayerCO", "isHero": false, "position": "CO", "initialStack": 10500 },
    { "id": 0, "name": "Hero (BTN)", "isHero": true, "position": "BTN", "initialStack": 10000 },
    { "id": 1, "name": "Villain1 (SB)", "isHero": false, "position": "SB", "initialStack": 9500 },
    { "id": 2, "name": "Villain2 (BB)", "isHero": false, "position": "BB", "initialStack": 12000 }
  ],
  "heroCards": { "holeCard1": "Ah", "holeCard2": "Kh" },
  "board": ["As", "Kd", "7h", "2c", "Qh"],
  "preflop": {
    "potSizeAtStart": 150, 
    "potSizeAtEnd": 1650,
    "cardsDealt": null,
    "actions": [
      { "playerId": 3, "type": "fold", "amount": null },
      { "playerId": 4, "type": "raise", "amount": 250 },   
      { "playerId": 5, "type": "fold", "amount": null },
      { "playerId": 0, "type": "raise", "amount": 750 },   
      { "playerId": 1, "type": "fold", "amount": null },   
      { "playerId": 2, "type": "fold", "amount": null },   
      { "playerId": 4, "type": "call", "amount": 500 }    
    ]
  },
  "flop": {
    "potSizeAtStart": 1650,
    "potSizeAtEnd": 3650,
    "cardsDealt": ["As", "Kd", "7h"],
    "actions": [
      { "playerId": 4, "type": "check", "amount": null },
      { "playerId": 0, "type": "bet", "amount": 1000 },
      { "playerId": 4, "type": "call", "amount": 1000 }
    ]
  },
  "turn": {
    "potSizeAtStart": 3650,
    "potSizeAtEnd": 3650,
    "cardsDealt": ["2c"],
    "actions": [
      { "playerId": 4, "type": "check", "amount": null },
      { "playerId": 0, "type": "check", "amount": null }
    ]
  },
  "river": {
    "potSizeAtStart": 3650,
    "potSizeAtEnd": 8650,
    "cardsDealt": ["Qh"],
    "actions": [
      { "playerId": 4, "type": "bet", "amount": 2500 },
      { "playerId": 0, "type": "call", "amount": 2500 }
    ]
  },
  "finalPot": 8650,
  "heroTotalContribution": 4250, 
  "declaredWinnerIds": [0],
  "isChoppedPot": false,
  "analysisJson": null 
};

// Sample Analysis Data - UPDATED to use 'streets' key
const sampleAnalysisJson = {
  version: "v1.0-sample",
  summary: {
    overallLineAssessment: "Hero played reasonably well.",
    overallRating: 7,
    heroProfileTag: "Standard Player",
    improvementFocus: ["Consider 3-betting preflop more often."]
  },
  streets: { 
    preflop: {
      rating: 8,
      analysis: "Preflop 3-bet with AKs from the button is standard against an MP open. Good sizing.",
      learningTips: ["Identify good spots for 3-betting light and for value."]
    },
    flop: {
      rating: 6,
      analysis: "C-betting top two pair on a dry board is good. The size is okay, could consider slightly larger to extract more value or deny equity.",
      learningTips: ["Size your c-bets effectively based on board texture and hand strength."]
    },
    turn: {
      rating: 7,
      analysis: "Checking back the turn after MP checks is a reasonable line to control the pot size or induce bluffs on the river.",
      learningTips: ["Understand when to check for pot control or to change the dynamic of the hand."]
    },
    river: {
      rating: 3,
      analysis: "Calling the river bet with top two pair was a mistake here. Given the large bet size and board texture, villain likely has a strong hand.",
      learningTips: ["Evaluate river bets carefully considering villain's range and tendencies."]
    }
  },
  keyMistakes: [
    { street: "flop", description: "Bet sizing could be improved slightly." }
  ]
};

const HandReplayerPage: React.FC = () => {

  const location = useLocation() as any;
  const navState = (location?.state || {}) as {
    handSummary?: HandSummaryFull;
    handId?: string;
  };

  // Core hand data and replay state
  const [activeHandData, setActiveHandData] = useState<HandSummaryFull>(navState.handSummary || detailedHandSummaryData);
  const [currentStreetKey, setCurrentStreetKey] = useState<Street>('preflop');
  const [currentActionIndex, setCurrentActionIndex] = useState<number>(-1);
  const [loadedAnalysis, setLoadedAnalysis] = useState<any | null>(null);
  const [activeCoachingText, setActiveCoachingText] = useState<string | null>(null);
  const [isCoachingVisible, setIsCoachingVisible] = useState<boolean>(false);
  const [lastActionPlayerId, setLastActionPlayerId] = useState<number | null>(null);
  const [lastActionType, setLastActionType] = useState<string | null>(null);
  const [lastActionAmount, setLastActionAmount] = useState<number | null>(null);
  

  // Custom JSON loading
  const [pastedJsonString, setPastedJsonString] = useState<string>('');
  const [jsonParseError, setJsonParseError] = useState<string | null>(null);

  // Tab States
  const [activeTabKey, setActiveTabKey] = useState<string>("controls");
  
  // Enhanced Mode System - four-mode workflow
  type AppMode = 'replay' | 'setup' | 'input' | 'submit';
  const [currentMode, setCurrentMode] = useState<AppMode>('replay');
  
  // Legacy Input Mode State (keeping for backwards compatibility)
  const [isInputMode, setIsInputMode] = useState<boolean>(false);

  // Sync legacy isInputMode with new mode system
  useEffect(() => {
    setIsInputMode(currentMode === 'input');
  }, [currentMode]);
  const [inputModeSettings, setInputModeSettings] = useState(() => {
    const maxPlayers = 9;
    const heroPosition: PlaygroundPosition = 'BTN';
    
    return {
      smallBlind: 1,
      bigBlind: 2,
      maxPlayers: maxPlayers,
      autoFoldPlayerIds: createInitialAutoFoldIds(maxPlayers, heroPosition),
      heroPosition: heroPosition
    };
  });
  
  // Add state to track which seat should show radial menu in input mode
  const [inputModeActivePlayerId, setInputModeActivePlayerId] = useState<number | null>(null);
  
  // Add state to track actions taken in input mode for feedback
  const [inputModeActions, setInputModeActions] = useState<Record<number, { action: ActionType; timestamp: number }>>({});
  
  // Add state to track player contributions in input mode
  const [inputModePlayerContributions, setInputModePlayerContributions] = useState<Record<number, number>>({});
  
  // Add state to track folded players in input mode
  const [inputModeFoldedPlayers, setInputModeFoldedPlayers] = useState<Set<number>>(new Set());

  // Add state to track stack sizes in input mode
  const [inputModeStackSizes, setInputModeStackSizes] = useState<Record<number, number>>(() => {
    // Use default values that match inputModeSettings initialization
    const seats = 9;           // Default maxPlayers from inputModeSettings
    const bb = 2;              // Default bigBlind from inputModeSettings  
    const sb = 1;              // Default smallBlind from inputModeSettings
    return getInitialStacks(seats, bb, sb);
  });

  // Add state for bet sizing dialog
  const [showBetSizingDialog, setShowBetSizingDialog] = useState(false);
  const [betSizingAction, setBetSizingAction] = useState<'bet' | 'raise'>('bet');
  const [betSizingSeatId, setBetSizingSeatId] = useState<number | null>(null);

  // Add state for setup popup
  const [showSetupPopup, setShowSetupPopup] = useState(false);

  // Add state for hero card selection in input mode
  const [inputModeHeroCards, setInputModeHeroCards] = useState<HeroCards>({ holeCard1: '', holeCard2: '' });
  const [inputModeCommunityCards, setInputModeCommunityCards] = useState<CommunityCards>({
    flopCard1: '', flopCard2: '', flopCard3: '', turnCard: '', riverCard: ''
  });
  const [showCardPicker, setShowCardPicker] = useState(false);
  const [cardPickerPosition, setCardPickerPosition] = useState<CardPosition>('card1');

  // Add action history for undo functionality
  const [inputModeActionHistory, setInputModeActionHistory] = useState<Array<{
    seatId: number;
    action: ActionType;
    amount?: number;
    timestamp: number;
    previousContribution: number;
    previousAutoFoldStatus: boolean;
    previousFoldedStatus: boolean;
    previousStack: number;
  }>>([]);

  // Add state for current street in input mode
  const [inputModeCurrentStreet, setInputModeCurrentStreet] = useState<Street>('preflop');

  // Add state for each street's data in input mode
  const [inputModeStreetData, setInputModeStreetData] = useState<Record<Street, {
    playerContributions: Record<number, number>;
    foldedPlayers: Set<number>;
    autoFoldPlayerIds: Set<number>;
    actions: Record<number, { action: ActionType; timestamp: number }>;
    actionHistory: Array<{
      seatId: number;
      action: ActionType;
      amount?: number;
      timestamp: number;
      previousContribution: number;
      previousAutoFoldStatus: boolean;
      previousFoldedStatus: boolean;
      previousStack: number;
    }>;
  }>>(() => {
    // Initialize with default data for each street
    const streetOrder: Street[] = ['preflop', 'flop', 'turn', 'river'];
    const initialStreetData: Partial<Record<Street, any>> = {};
    
    // Create initial auto-fold set for all streets (exclude SB, BB, and Hero)
    const maxPlayers = 9; // Default value
    const heroPosition: PlaygroundPosition = 'BTN'; // Default value
    const initialAutoFoldIds = createInitialAutoFoldIds(maxPlayers, heroPosition);
    
    streetOrder.forEach(street => {
      if (street === 'preflop') {
        // Preflop starts with blinds and only SB/BB/Hero active
        initialStreetData[street] = {
          playerContributions: { 1: 1, 2: 2 }, // Default blind values
          foldedPlayers: new Set(),
          autoFoldPlayerIds: initialAutoFoldIds,
          actions: {},
          actionHistory: []
        };
      } else {
        // Other streets inherit the auto-fold status from preflop initially
        // This prevents the bug where all players become active on flop/turn/river
        initialStreetData[street] = {
          playerContributions: {},
          foldedPlayers: new Set(),
          autoFoldPlayerIds: new Set(initialAutoFoldIds), // Copy the auto-fold status
          actions: {},
          actionHistory: []
        };
      }
    });
    
    return initialStreetData as Record<Street, any>;
  });

  // Fetch analysis when navigated with a handId and hydrate server summary if present
  useEffect(() => {
    let cancelled = false;
    const handIdToLoad = navState.handId;
    if (!handIdToLoad) return;

    const fetchAnalysis = async () => {
      try {
        const res = await PlayFab.executeCloudScript('getDynamoDBData', {
          action: 'getHand',
          handId: handIdToLoad,
          ownerPlayfabId: PlayFab.getPlayerId()
        });
        const nested = (res as any)?.data?.FunctionResult?.FunctionResult;
        if (!cancelled && res?.success && nested?.success) {
          const handAnalysis = nested.data;
          if (handAnalysis?.summaryJson && !navState.handSummary) {
            try {
              const serverSummary = typeof handAnalysis.summaryJson === 'string' ? JSON.parse(handAnalysis.summaryJson) : handAnalysis.summaryJson;
              if (serverSummary?.metadata && serverSummary?.preflop) {
                setActiveHandData(serverSummary as HandSummaryFull);
              }
            } catch {}
          }
          setLoadedAnalysis(handAnalysis);
        }
      } catch (e) {
        console.warn('Failed to fetch analysis for handId', handIdToLoad, e);
      }
    };

    fetchAnalysis();
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (cancelled || attempts > 4 || loadedAnalysis) {
        clearInterval(interval);
        return;
      }
      fetchAnalysis();
    }, 3000);

    return () => { cancelled = true; clearInterval(interval); };
  }, [navState.handId]);

  // Keep coaching text synced with loaded analysis and current street
  useEffect(() => {
    const s = loadedAnalysis?.streets?.[currentStreetKey]?.analysis
      ?? loadedAnalysis?.summary?.overallLineAssessment
      ?? null;
    setActiveCoachingText(s);
    setIsCoachingVisible(!!s);
  }, [loadedAnalysis, currentStreetKey]);

  // Derived state from activeHandData and current replay state
  const streetsOrder: Street[] = useMemo(() => ['preflop', 'flop', 'turn', 'river'], []);
  const currentStreetData = useMemo((): HandSummaryStreet | null => {
    if (!activeHandData) return null;
    return activeHandData[currentStreetKey];
  }, [currentStreetKey, activeHandData]);
  const allPlayersInHand = useMemo(() => {
    if (!activeHandData) return [];
    let players = [...activeHandData.players];
    const playerActions = [
        ...(activeHandData.preflop?.actions || []),
        ...(activeHandData.flop?.actions || []),
        ...(activeHandData.turn?.actions || []),
        ...(activeHandData.river?.actions || [])
    ];
    playerActions.forEach(action => {
        if (action.playerId !== null && !players.find(p => p.id === action.playerId)) {
            console.warn(`Player ID ${action.playerId} in actions not found in main player list. Adding placeholder.`);
            players.push({
                id: action.playerId,
                name: `Player ${action.playerId}`,
                isHero: false,
                position: "UTG", 
                initialStack: 0 
            });
        }
    });
    return players;
  }, [activeHandData]);
  const seatCount = useMemo(() => allPlayersInHand.length, [allPlayersInHand]);
  const seats = useMemo((): SeatMeta[] => {
    return allPlayersInHand.map(p => ({
      id: p.id,
      label: p.position, 
      tag: p.position === 'SB' ? 'SB' : p.position === 'BB' ? 'BB' : undefined
    }));
  }, [allPlayersInHand]);
  const heroSeatId = useMemo(() => {
    if (!activeHandData) return null;
    const hero = allPlayersInHand.find(p => p.isHero);
    return hero ? hero.id : null;
  }, [allPlayersInHand, activeHandData]);
  const initialStackSizes = useMemo(() => {
    if (!activeHandData) return {};
    const stacks: Record<number, number> = {};
    allPlayersInHand.forEach(p => {
      stacks[p.id] = p.initialStack;
    });
    return stacks;
  }, [allPlayersInHand, activeHandData]);
  const [currentStackSizes, setCurrentStackSizes] = useState<Record<number, number>>(initialStackSizes);
  const [currentPotSize, setCurrentPotSize] = useState<number>(activeHandData.preflop.potSizeAtStart);
  const [displayedCommunityCards, setDisplayedCommunityCards] = useState<string[]>([]);
  const [foldedPlayerIds, setFoldedPlayerIds] = useState<Set<number>>(new Set());
  const [currentPlayerToActId, setCurrentPlayerToActId] = useState<number | null>(null);
  const [currentBet, setCurrentBet] = useState<number>(0); 
  const [playerStreetContributions, setPlayerStreetContributions] = useState<Record<number,number>>({});

  // Analysis submission state
  const [isSubmittingAnalysis, setIsSubmittingAnalysis] = useState<boolean>(false);
  const [analysisSubmissionStatus, setAnalysisSubmissionStatus] = useState<string | null>(null);

  // Floating toolbar dialog state
  const [toolbarDialogType, setToolbarDialogType] = useState<string | null>(null);

  // Auto-play functionality using custom hook
  const {
    isAutoPlaying,
    autoPlaySpeed,
    playToNextCoaching,
    handlePlayPause,
    handlePlayToNextCoaching,
    handleSpeedChange,
    handleResetToStart,
    hasCoachingAtPosition
  } = useAutoPlay({
    activeHandData,
    currentStreetKey,
    currentActionIndex,
    currentStreetData,
    streetsOrder,
    allPlayersInHand,
    loadedAnalysis,
    onActionIndexChange: setCurrentActionIndex,
    onStreetChange: setCurrentStreetKey,
    onCoachingChange: (text, visible) => {
      setActiveCoachingText(text);
      setIsCoachingVisible(visible);
    },
    onLastActionChange: (playerId, type, amount) => {
      setLastActionPlayerId(playerId);
      setLastActionType(type);
      setLastActionAmount(amount);
    },
    onPlayerContributionsChange: setPlayerStreetContributions
  });

  // Helper function to save current street data
  const saveCurrentStreetData = () => {
    if (!isInputMode) return;
    
    setInputModeStreetData(prev => ({
      ...prev,
      [inputModeCurrentStreet]: {
        playerContributions: inputModePlayerContributions,
        foldedPlayers: inputModeFoldedPlayers,
        autoFoldPlayerIds: inputModeSettings.autoFoldPlayerIds,
        actions: inputModeActions,
        actionHistory: inputModeActionHistory
      }
    }));
  };

  // Helper function to load street data
  const loadStreetData = (street: Street) => {
    const streetData = inputModeStreetData[street];
    
    // Update all the individual state items
    setInputModePlayerContributions(streetData.playerContributions);
    setInputModeFoldedPlayers(streetData.foldedPlayers);
    setInputModeSettings(prev => ({
      ...prev,
      autoFoldPlayerIds: streetData.autoFoldPlayerIds
    }));
    setInputModeActions(streetData.actions);
    setInputModeActionHistory(streetData.actionHistory);
  };

  // Handler for switching to any street
  const handleSwitchToStreet = (targetStreet: Street) => {
    if (targetStreet === inputModeCurrentStreet) return;
    
    console.log(`[INPUT MODE] Switching from ${inputModeCurrentStreet} to ${targetStreet}`);
    
    // Save current street data first
    saveCurrentStreetData();
    
    // Update current street
    setInputModeCurrentStreet(targetStreet);
    
    // Load the target street's data
    const streetData = inputModeStreetData[targetStreet];
    
    // For post-flop streets, we need to carry forward the active player status from previous streets
    if (targetStreet !== 'preflop') {
      // Start with the current auto-fold status (which reflects who has taken actions)
      const currentAutoFoldIds = inputModeSettings.autoFoldPlayerIds;
      
      // On post-flop, we want to separate:
      // 1. Players who actually folded on this specific street (should remain visible if they folded on current street)
      // 2. Auto-fold players who should be hidden
      
      // Load street data but handle folded players correctly
      setInputModePlayerContributions(streetData.playerContributions);
      
      // FIXED: Accumulate folded players from all previous streets + this street
      // Instead of overwriting, we need to preserve players who folded on previous streets
      setInputModeFoldedPlayers(prev => {
        const allFoldedPlayers = new Set(prev); // Keep current folded players
        streetData.foldedPlayers.forEach(playerId => allFoldedPlayers.add(playerId)); // Add any who folded on this street
        return allFoldedPlayers;
      });
      setInputModeSettings(prev => ({
        ...prev,
        autoFoldPlayerIds: currentAutoFoldIds // Use current auto-fold status, not stored street data
      }));
      setInputModeActions(streetData.actions);
      setInputModeActionHistory(streetData.actionHistory);
      
      console.log(`[INPUT MODE] Switched to ${targetStreet} with carried-forward auto-fold status:`, Array.from(currentAutoFoldIds));
      console.log(`[INPUT MODE] Players who folded specifically on ${targetStreet}:`, Array.from(streetData.foldedPlayers));
      console.log(`[INPUT MODE] Total folded players (cumulative):`, Array.from(inputModeFoldedPlayers));
      console.log(`[INPUT MODE] Auto-fold players (will be hidden):`, Array.from(currentAutoFoldIds));
    } else {
      // For preflop, use the stored data as-is
      loadStreetData(targetStreet);
      console.log(`[INPUT MODE] Switched to preflop with stored auto-fold status:`, Array.from(streetData.autoFoldPlayerIds));
    }
    
    // Clear any active radial menu
    setInputModeActivePlayerId(null);
    
    console.log(`[INPUT MODE] Successfully switched to ${targetStreet}`);
  };



  // Effect to update state based on currentStreetKey and currentActionIndex
  useEffect(() => {
    if (!activeHandData) return;

    let newPotSize = activeHandData.preflop.potSizeAtStart; 
    let newStackSizes = { ...initialStackSizes }; 
    let newFoldedPlayerIds = new Set<number>();
    let newCommunityCards: string[] = [];
    let nextPlayer: number | null = null;
    let streetBet = 0; 
    let tempPlayerStreetContributions: Record<number, number> = {};
    allPlayersInHand.forEach(p => { tempPlayerStreetContributions[p.id] = 0; });

    const sbPlayerInfo = allPlayersInHand.find(p => p.position === 'SB');
    const bbPlayerInfo = allPlayersInHand.find(p => p.position === 'BB');
    const sbAmount = activeHandData.metadata.stakes.smallBlind;
    const bbAmount = activeHandData.metadata.stakes.bigBlind;
    
    const blindContributions: Record<number, number> = {};
    if (sbPlayerInfo) {
      blindContributions[sbPlayerInfo.id] = sbAmount;
    }
    if (bbPlayerInfo) {
      blindContributions[bbPlayerInfo.id] = bbAmount;
    }

    if (currentStreetKey === 'preflop') {
      Object.keys(blindContributions).forEach(playerId => {
        const numPlayerId = Number(playerId);
        tempPlayerStreetContributions[numPlayerId] = blindContributions[numPlayerId];
        if (newStackSizes[numPlayerId] !== undefined) {
          newStackSizes[numPlayerId] -= blindContributions[numPlayerId];
        }
      });
      streetBet = bbAmount;
    }

    let breakLoop = false;
    for (const street of streetsOrder) {
      if (breakLoop) break;
      const streetData = activeHandData[street];
      if (!streetData) continue;

      if (street === 'flop' && activeHandData.board && activeHandData.board.length >= 3 && 
          streetsOrder.indexOf(street) <= streetsOrder.indexOf(currentStreetKey)) {
        newCommunityCards = activeHandData.board.slice(0, 3);
      } else if (street === 'turn' && activeHandData.board && activeHandData.board.length >= 4 && 
                 streetsOrder.indexOf(street) <= streetsOrder.indexOf(currentStreetKey)) {
        newCommunityCards = activeHandData.board.slice(0, 4);
      } else if (street === 'river' && activeHandData.board && activeHandData.board.length >= 5 && 
                 streetsOrder.indexOf(street) <= streetsOrder.indexOf(currentStreetKey)) {
        newCommunityCards = activeHandData.board.slice(0, 5);
      }

      if (streetsOrder.indexOf(street) > streetsOrder.indexOf(currentStreetKey)) {
        break; 
      }

      if (street === currentStreetKey) {
        newPotSize = streetData.potSizeAtStart; 
        if (street !== 'preflop') {
          allPlayersInHand.forEach(p => {
            tempPlayerStreetContributions[p.id] = 0;
          });
          streetBet = 0;
        } 
        if (currentActionIndex === -1) {
          setLastActionPlayerId(null);
          setLastActionType(null);
          setLastActionAmount(null);
        }
      }

      for (let i = 0; i < streetData.actions.length; i++) {
        if (street === currentStreetKey && i > currentActionIndex) {
          if (i === currentActionIndex + 1) {
            const actionAboutToHappen = streetData.actions[i];
            nextPlayer = actionAboutToHappen.playerId;
          }
          breakLoop = true;
          break;
        }

        const action = streetData.actions[i];
        
        if (action.amount && action.amount > 0) {
            newStackSizes[action.playerId] -= action.amount;
        }
        
        newPotSize += (action.amount || 0);
        
        const currentContribution = tempPlayerStreetContributions[action.playerId] || 0;
        tempPlayerStreetContributions[action.playerId] = currentContribution + (action.amount || 0);

        if (street === currentStreetKey) {
          setLastActionPlayerId(action.playerId);
          setLastActionType(action.type);
          setLastActionAmount(action.amount);
        }

        if (action.type === 'fold') {
          newFoldedPlayerIds.add(action.playerId);
        }

        if (action.type === 'bet' || action.type === 'raise') {
            streetBet = tempPlayerStreetContributions[action.playerId];
        }
      }

      if (street === currentStreetKey) {
         setCurrentBet(streetBet);
         if (currentActionIndex >= streetData.actions.length - 1) {
             nextPlayer = null; 
         }
      }
      
      console.log(`[HandReplayerPage] Processing street: ${street}, Current street: ${currentStreetKey}`);
    }

    setCurrentPotSize(newPotSize);
    setCurrentStackSizes(newStackSizes); 
    setDisplayedCommunityCards(newCommunityCards);
    setFoldedPlayerIds(newFoldedPlayerIds);
    setCurrentPlayerToActId(nextPlayer);
    
    console.log(`[HandReplayerPage] Street: ${currentStreetKey}, ActionIndex: ${currentActionIndex}, Contributions:`, tempPlayerStreetContributions);
    
    if (currentStreetKey === 'preflop') {
      const sbPlayerId = sbPlayerInfo?.id;
      const bbPlayerId = bbPlayerInfo?.id;
      
      if (sbPlayerId && !tempPlayerStreetContributions[sbPlayerId]) {
        tempPlayerStreetContributions[sbPlayerId] = sbAmount;
        console.log(`[HandReplayerPage] Enforcing SB visibility for player ${sbPlayerId}: ${sbAmount}`);
      }
      
      if (bbPlayerId && !tempPlayerStreetContributions[bbPlayerId]) {
        tempPlayerStreetContributions[bbPlayerId] = bbAmount;
        console.log(`[HandReplayerPage] Enforcing BB visibility for player ${bbPlayerId}: ${bbAmount}`);
      }
    }
    
    setPlayerStreetContributions(tempPlayerStreetContributions); 

    if (loadedAnalysis && loadedAnalysis.streets && loadedAnalysis.streets[currentStreetKey]) {
      const streetAnalysisData = loadedAnalysis.streets[currentStreetKey];
      let shouldShowCoaching = false;
      
      if (currentStreetData && currentActionIndex >= 0 && currentActionIndex < currentStreetData.actions.length) {
        const currentAction = currentStreetData.actions[currentActionIndex];
        const currentActionPlayer = allPlayersInHand.find(p => p.id === currentAction.playerId);
        shouldShowCoaching = currentActionPlayer?.isHero === true;
      }
      
      if (shouldShowCoaching && streetAnalysisData?.analysis) {
        setActiveCoachingText(streetAnalysisData.analysis);
        setIsCoachingVisible(true);
      } else {
        setActiveCoachingText(null);
        setIsCoachingVisible(false);
      }
    } else {
      setActiveCoachingText(null);
      setIsCoachingVisible(false);
    }

  }, [currentStreetKey, currentActionIndex, activeHandData, streetsOrder, initialStackSizes, loadedAnalysis, allPlayersInHand]);

  // Auto-switch to coaching tab when coaching content becomes available
  useEffect(() => {
    if (activeCoachingText && isCoachingVisible) {
      setActiveTabKey("coaching");
    }
  }, [activeCoachingText, isCoachingVisible]);

  const handleNextAction = () => {
    if (!currentStreetData) return;
    if (currentActionIndex < currentStreetData.actions.length - 1) {
      setCurrentActionIndex(currentActionIndex + 1);
      setActiveCoachingText(null); 
      setIsCoachingVisible(false); 
    } else {
      const currentStreetNum = streetsOrder.indexOf(currentStreetKey);
      if (currentStreetNum < streetsOrder.length - 1) {
        const nextStreet = streetsOrder[currentStreetNum + 1];
        setCurrentStreetKey(nextStreet);
        setCurrentActionIndex(-1);
        setLastActionPlayerId(null);
        setLastActionType(null);
        setLastActionAmount(null);
        
        let emptyContributions: Record<number, number> = {};
        allPlayersInHand.forEach(p => { emptyContributions[p.id] = 0; });
        setPlayerStreetContributions(emptyContributions);
        
      } else {
        console.log("End of hand");
        setActiveCoachingText(null);
        setIsCoachingVisible(false); 
        setLastActionPlayerId(null);
        setLastActionType(null);
        setLastActionAmount(null);
      }
    }
  };

  const handlePrevAction = () => {
    setActiveCoachingText(null); 
    setIsCoachingVisible(false); 
    if (currentActionIndex > -1) {
      setCurrentActionIndex(currentActionIndex - 1);
    } else {
      const currentStreetNum = streetsOrder.indexOf(currentStreetKey);
      if (currentStreetNum > 0) {
        const prevStreetKey = streetsOrder[currentStreetNum - 1];
        const prevStreetData = activeHandData[prevStreetKey];
        setCurrentStreetKey(prevStreetKey);
        setCurrentActionIndex(prevStreetData ? prevStreetData.actions.length - 1 : -1);
        setLastActionPlayerId(null);
        setLastActionType(null);
        setLastActionAmount(null);
        
        let emptyContributions: Record<number, number> = {};
        allPlayersInHand.forEach(p => { emptyContributions[p.id] = 0; });
        setPlayerStreetContributions(emptyContributions);
      } else {
        console.log("Start of hand");
        setLastActionPlayerId(null);
        setLastActionType(null);
        setLastActionAmount(null);
      }
    }
  };

  const handleLoadAnalysis = () => {
    setActiveHandData(detailedHandSummaryData); 
    setLoadedAnalysis(sampleAnalysisJson);       
    setCurrentStreetKey('preflop');             
    setCurrentActionIndex(-1);                  
    
    setActiveCoachingText(null);
    setIsCoachingVisible(false);
    console.log("Loaded DETAILED sample hand and analysis:", detailedHandSummaryData, sampleAnalysisJson);
  };

  const handleParseAndLoadCustomJson = () => {
    if (!pastedJsonString.trim()) {
      setJsonParseError("Text area is empty. Paste your hand JSON.");
      return;
    }
    try {
      const customHandData = JSON.parse(pastedJsonString) as HandSummaryFull;
      if (!customHandData.metadata || !customHandData.players || !customHandData.preflop) {
        throw new Error("Invalid hand JSON structure. Missing required fields like metadata, players, or preflop.");
      }

      setActiveHandData(customHandData);
      setLoadedAnalysis(null); 
      setActiveCoachingText(null);
      setIsCoachingVisible(false);
      setCurrentStreetKey('preflop');
      setCurrentActionIndex(-1);
      setJsonParseError(null);
      setPastedJsonString('');     
      console.log("Loaded CUSTOM hand data:", customHandData);
    } catch (error) {
      console.error("Error parsing custom JSON:", error);
      setJsonParseError(error instanceof Error ? error.message : "An unknown error occurred during parsing.");
    }
  };

  const handleStackChange = (seatId: number, value: string) => {
    console.log(`Stack change for seat ${seatId}: ${value}`);
  };

  // Mode switching functions for enhanced workflow
  const handleModeSwitch = (newMode: AppMode) => {
    setCurrentMode(newMode);
    
    if (newMode === 'setup') {
      setShowSetupPopup(true);
    } else if (newMode === 'input') {
      // Reset to clean input state
      setCurrentStreetKey('preflop');
      setCurrentActionIndex(-1);
      setActiveCoachingText(null);
      setIsCoachingVisible(false);
      setInputModeCurrentStreet('preflop');
      setInputModeActivePlayerId(null);
    } else if (newMode === 'replay') {
      // Ensure we're in replay state
      setActiveCoachingText(null);
      setIsCoachingVisible(false);
      setInputModeActivePlayerId(null);
    }
  };

  const handleSubmitHand = () => {
    // TODO: Validate hand completeness
    // TODO: Convert input mode data to replay format
    console.log("Submitting hand for replay...");
    setCurrentMode('replay');
  };

  const handleSetupConfirm = (settings: any) => {
    setInputModeSettings(settings);
    
    // Initialize blind contributions
    const initialContributions: Record<number, number> = {};
    const sbSeatId = getSeatIdFromPosition('SB', settings.maxPlayers);
    const bbSeatId = getSeatIdFromPosition('BB', settings.maxPlayers);
    
    if (sbSeatId !== null) {
      initialContributions[sbSeatId] = settings.smallBlind;
    }
    if (bbSeatId !== null) {
      initialContributions[bbSeatId] = settings.bigBlind;
    }
    
    setInputModePlayerContributions(initialContributions);
    
    // Initialize stack sizes with blinds deducted
    const defaultStack = settings.bigBlind * 100;
    const initialStacks: Record<number, number> = {};
    
    for (let i = 0; i < settings.maxPlayers; i++) {
      if (!settings.autoFoldPlayerIds.has(i)) {
        if (i === sbSeatId) {
          initialStacks[i] = defaultStack - settings.smallBlind;
        } else if (i === bbSeatId) {
          initialStacks[i] = defaultStack - settings.bigBlind;
        } else {
          initialStacks[i] = defaultStack;
        }
      }
    }
    
    setInputModeStackSizes(initialStacks);
    
    setShowSetupPopup(false);
    setCurrentMode('input');
    console.log("Setup confirmed, entering input mode:", settings);
  };

  const handleSetupCancel = () => {
    setShowSetupPopup(false);
    setCurrentMode('replay');
  };

  // Input Mode Handlers - moved before useMemo
  const handleToggleInputMode = () => {
    setIsInputMode(!isInputMode);
    if (!isInputMode) {
      // Entering input mode - reset to clean state
      setCurrentStreetKey('preflop');
      setCurrentActionIndex(-1);
      setActiveCoachingText(null);
      setIsCoachingVisible(false);
      setInputModeActivePlayerId(null); // Clear any active radial menu
      setInputModeActions({}); // Clear any previous actions
      setInputModeActionHistory([]); // Clear action history
      setInputModeCurrentStreet('preflop'); // Reset to preflop
      
      // Reset street data to initial state
      const streetOrder: Street[] = ['preflop', 'flop', 'turn', 'river'];
      const initialStreetData: Partial<Record<Street, any>> = {};
      
      // Create initial auto-fold set for all streets (exclude SB, BB, and Hero)
      const initialAutoFoldIds = createInitialAutoFoldIds(inputModeSettings.maxPlayers, inputModeSettings.heroPosition);
      
      streetOrder.forEach(street => {
        if (street === 'preflop') {
          // Preflop starts with blinds and only SB/BB/Hero active
          initialStreetData[street] = {
            playerContributions: { 1: inputModeSettings.smallBlind, 2: inputModeSettings.bigBlind },
            foldedPlayers: new Set(),
            autoFoldPlayerIds: initialAutoFoldIds,
            actions: {},
            actionHistory: []
          };
        } else {
          // Other streets inherit the auto-fold status from preflop initially
          // This prevents the bug where all players become active on flop/turn/river
          initialStreetData[street] = {
            playerContributions: {},
            foldedPlayers: new Set(),
            autoFoldPlayerIds: new Set(initialAutoFoldIds), // Copy the auto-fold status
            actions: {},
            actionHistory: []
          };
        }
      });
      
      setInputModeStreetData(initialStreetData as Record<Street, any>);
      
      // Initialize contributions with blinds for preflop
      const initialContributions: Record<number, number> = {};
      initialContributions[1] = inputModeSettings.smallBlind; // SB
      initialContributions[2] = inputModeSettings.bigBlind;   // BB
      setInputModePlayerContributions(initialContributions);
      setInputModeFoldedPlayers(new Set()); // Clear folded players
      
      // Initialize stack sizes and deduct blinds
      const initialStackSizes = getInitialStacks(
        inputModeSettings.maxPlayers,
        inputModeSettings.bigBlind,
        inputModeSettings.smallBlind
      );
      setInputModeStackSizes(initialStackSizes);
      
      // Use the same auto-fold settings that were already created
      setInputModeSettings(prev => ({
        ...prev,
        autoFoldPlayerIds: initialAutoFoldIds
      }));
      
      // Clear bet sizing state
      setShowBetSizingDialog(false);
      setBetSizingSeatId(null);
    } else {
      // Exiting input mode - clear input mode state
      setInputModeActivePlayerId(null);
      setInputModeActions({}); // Clear action indicators
      setInputModeActionHistory([]); // Clear action history
      setInputModeCurrentStreet('preflop'); // Reset street
      setInputModePlayerContributions({}); // Clear contributions
      setInputModeFoldedPlayers(new Set()); // Clear fol  ded players
      
      // Clear bet sizing state
      setShowBetSizingDialog(false);
      setBetSizingSeatId(null);
    }
  };

  const handleSeatToggle = (seatId: number) => {
    console.log(`[handleSeatToggle] Called with seatId: ${seatId}, isInputMode: ${isInputMode}`);
    
    if (!isInputMode) {
      console.log(`[handleSeatToggle] Returning early - not in input mode`);
      return;
    }
    
    setInputModeSettings(prev => {
      const newAutoFoldIds = new Set(prev.autoFoldPlayerIds);
      const wasAutoFold = newAutoFoldIds.has(seatId);
      
      if (wasAutoFold) {
        // Remove from auto-fold (player becomes active)
        newAutoFoldIds.delete(seatId);
        console.log(`[handleSeatToggle] Seat ${seatId} changed from auto-fold to active`);
      } else {
        // Add to auto-fold
        newAutoFoldIds.add(seatId);
        console.log(`[handleSeatToggle] Seat ${seatId} changed from active to auto-fold`);
      }
      return { ...prev, autoFoldPlayerIds: newAutoFoldIds };
    });
  };

  const handleInputModeSettingsChange = (field: string, value: any) => {
    setInputModeSettings(prev => {
      if (field === 'maxPlayers') {
        // When changing player count, reset auto-fold to only SB, BB, and Hero active
        return {
          ...prev,
          [field]: value,
          autoFoldPlayerIds: createInitialAutoFoldIds(value, prev.heroPosition),
          heroPosition: 'BTN' // Reset hero position when changing player count
        };
      }
      return {
        ...prev,
        [field]: value
      };
    });
  };


    


 

  // Handler for clicking on bet amounts to remove them
  const handleBetClick = (seatId: number) => {
    if (!isInputMode) return;
    
    console.log(`[INPUT MODE] Bet clicked for seat ${seatId} - removing bet and reactivating seat`);
    
    // Record current state for undo functionality
    const currentContribution = inputModePlayerContributions?.[seatId] || 0;
    const currentAutoFoldStatus = inputModeSettings.autoFoldPlayerIds.has(seatId);
    const currentFoldedStatus = inputModeFoldedPlayers.has(seatId);
    const currentStack = inputModeStackSizes[seatId] ?? 0;
    
    // Remove the bet contribution
    setInputModePlayerContributions(prev => {
      const newContributions = { ...prev };
      
      // For preflop, preserve blinds
      if (inputModeCurrentStreet === 'preflop') {
        if (seatId === 1) { // SB
          newContributions[seatId] = inputModeSettings.smallBlind;
        } else if (seatId === 2) { // BB
          newContributions[seatId] = inputModeSettings.bigBlind;
        } else {
          newContributions[seatId] = 0;
        }
      } else {
        // For other streets, remove all contribution
        newContributions[seatId] = 0;
      }
      
      return newContributions;
    });
    
    // Remove from folded players if they were folded
    setInputModeFoldedPlayers(prev => {
      const newFoldedPlayers = new Set(prev);
      newFoldedPlayers.delete(seatId);
      return newFoldedPlayers;
    });
    
    // Reactivate the seat (remove from auto-fold)
    setInputModeSettings(prev => {
      const newAutoFoldIds = new Set(prev.autoFoldPlayerIds);
      newAutoFoldIds.delete(seatId);
      return {
        ...prev,
        autoFoldPlayerIds: newAutoFoldIds
      };
    });
    
    // Remove action indicator
    setInputModeActions(prev => {
      const newActions = { ...prev };
      delete newActions[seatId];
      return newActions;
    });
    
    // Add to action history for undo functionality (this is a "remove bet" action)
    setInputModeActionHistory(prev => [...prev, {
      seatId,
      action: 'check' as ActionType, // Use check as placeholder for "remove bet"
      timestamp: Date.now(),
      previousContribution: currentContribution,
      previousAutoFoldStatus: currentAutoFoldStatus,
      previousFoldedStatus: currentFoldedStatus,
      previousStack: currentStack // Assuming previousStack is 0 for this action
    }]);
    
    console.log(`[INPUT MODE] Seat ${seatId} bet removed and reactivated`);
  };

  // Hero card selection handlers for input mode
  const handleHeroCard1Click = () => {
    if (!isInputMode) return;
    setCardPickerPosition('card1');
    setShowCardPicker(true);
  };

  const handleHeroCard2Click = () => {
    if (!isInputMode) return;
    setCardPickerPosition('card2');
    setShowCardPicker(true);
  };

  const handleCardSelect = (card: string) => {
    console.log(`[INPUT MODE] Card selected: ${card} for position: ${cardPickerPosition}`);
    
    if (isHeroCardPosition(cardPickerPosition)) {
      // Handle hero cards using utility function
      setInputModeHeroCards(prev => {
        const newCards = updateHeroCards(prev, cardPickerPosition, card);
        
        // Check for auto-transition using utility function
        const transition = shouldAutoTransition(cardPickerPosition, prev, inputModeCommunityCards);
        if (transition.shouldTransition && transition.nextPosition) {
          setTimeout(() => {
            setCardPickerPosition(transition.nextPosition!);
            // Keep the dialog open for the next card
          }, 100);
          return newCards;
        }
        
        // Close dialog if no auto-transition
        setShowCardPicker(false);
        return newCards;
      });
    } else if (isCommunityCardPosition(cardPickerPosition)) {
      // Handle community cards using utility function
      setInputModeCommunityCards(prev => {
        const newCards = updateCommunityCards(prev, cardPickerPosition, card);
        
        // Check for auto-transition using utility function
        const transition = shouldAutoTransition(cardPickerPosition, inputModeHeroCards, prev);
        if (transition.shouldTransition && transition.nextPosition) {
          setTimeout(() => {
            setCardPickerPosition(transition.nextPosition!);
            // Keep the dialog open for the next card
          }, 100);
          return newCards;
        }
        
        // Close dialog if no auto-transition
        setShowCardPicker(false);
        return newCards;
      });
    }
  };

  const handleCardPickerCancel = () => {
    setShowCardPicker(false);
  };

  // Quick set both hero cards function
  const handleSetBothHeroCards = () => {
    if (!isInputMode) return;
    setCardPickerPosition('card1');
    setShowCardPicker(true);
  };

  // Community card selection handlers for input mode
  const handleFlopCard1Click = () => {
    if (!isInputMode) return;
    setCardPickerPosition('flop1');
    setShowCardPicker(true);
  };

  const handleFlopCard2Click = () => {
    if (!isInputMode) return;
    setCardPickerPosition('flop2');
    setShowCardPicker(true);
  };

  const handleFlopCard3Click = () => {
    if (!isInputMode) return;
    setCardPickerPosition('flop3');
    setShowCardPicker(true);
  };

  const handleTurnCardClick = () => {
    if (!isInputMode) return;
    setCardPickerPosition('turn');
    setShowCardPicker(true);
  };

  const handleRiverCardClick = () => {
    if (!isInputMode) return;
    setCardPickerPosition('river');
    setShowCardPicker(true);
  };

  // Get used cards (to prevent selecting the same card twice for hero cards)
  const getUsedCards = (): string[] => {
    return getUsedCardsUtil(inputModeHeroCards, inputModeCommunityCards, cardPickerPosition);
  };

  // Convert HandSummaryFull to format expected by analysis system
  const convertToAnalysisFormat = (handData: HandSummaryFull): any => {
    const convertStreetActions = (actions: HandSummaryStreetAction[]) => {
      return actions.map(action => ({
        playerId: action.playerId,
        type: action.type,
        amount: action.amount === null ? undefined : action.amount
      }));
    };

    const convertStreet = (street: HandSummaryStreet) => ({
      potSizeAtStart: street.potSizeAtStart,
      potSizeAtEnd: street.potSizeAtEnd,
      cardsDealt: street.cardsDealt === null ? undefined : street.cardsDealt,
      actions: convertStreetActions(street.actions)
    });

    return {
      metadata: handData.metadata,
      players: handData.players,
      heroCards: handData.heroCards,
      board: handData.board === null ? undefined : handData.board,
      preflop: convertStreet(handData.preflop),
      flop: convertStreet(handData.flop),
      turn: convertStreet(handData.turn),
      river: convertStreet(handData.river),
      finalPot: handData.finalPot,
      heroTotalContribution: handData.heroTotalContribution,
      declaredWinnerIds: handData.declaredWinnerIds === null ? undefined : handData.declaredWinnerIds,
      isChoppedPot: handData.isChoppedPot === null ? undefined : handData.isChoppedPot
    };
  };

  // Submit hand for analysis using existing PlayFab system
  const submitHandForAnalysis = async (handData: HandSummaryFull, customInstructions?: string) => {
    try {
      const convertedData = convertToAnalysisFormat(handData);
      console.log('Original hand data:', handData);
      console.log('Converted data for analysis:', convertedData);
      
      const functionParameter = {
        summary: JSON.stringify(convertedData),
        handId: handData.metadata.handId,
        handType: handData.metadata.handType,
        sessionId: `replayer-${Date.now()}`,
        smallBlind: handData.metadata.stakes.smallBlind,
        bigBlind: handData.metadata.stakes.bigBlind,
        customInstructions: customInstructions || "Please analyze this hand",
        shouldAwardXP: true,
        playfabId: PlayFab.getPlayerId()
      };
      
      console.log('Function parameter being sent:', functionParameter);
      
      const result = await PlayFab.executeCloudScript({
        FunctionName: "analyzePokerHand",
        FunctionParameter: functionParameter
      });

      console.log('PlayFab response:', result);
      
      if (result.data?.FunctionResult?.success) {
        console.log('Hand submitted for analysis successfully');
        return result.data.FunctionResult;
      } else {
        console.error('Failed to submit hand - Full response:', result);
        console.error('FunctionResult:', result.data?.FunctionResult);
        const errorMessage = result.data?.FunctionResult?.message || result.data?.FunctionResult?.error || 'Failed to submit hand for analysis';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error submitting hand for analysis:', error);
      throw error;
    }
  };

  // Convert input mode data to proper hand summary format
  const convertInputModeToHandSummary = (): HandSummaryFull => {
    const streets: Street[] = ['preflop', 'flop', 'turn', 'river'];
    const streetActions: Record<Street, HandSummaryStreetAction[]> = {
      preflop: [],
      flop: [],
      turn: [],
      river: []
    };
    const streetPots: Record<Street, {start: number, end: number}> = {
      preflop: { start: 0, end: 0 },
      flop: { start: 0, end: 0 },
      turn: { start: 0, end: 0 },
      river: { start: 0, end: 0 }
    };

    // Calculate blinds contribution
    let currentPot = 0;
    if (inputModeSettings.maxPlayers > 2) {
      // Add small blind and big blind to preflop pot
      currentPot = inputModeSettings.smallBlind + inputModeSettings.bigBlind;
      streetPots.preflop.start = 0;
      streetPots.preflop.end = currentPot;
      
      // Add blind actions
      streetActions.preflop.push({
        playerId: getSeatIdFromPosition('SB', inputModeSettings.maxPlayers),
        type: 'post',
        amount: inputModeSettings.smallBlind
      });
      streetActions.preflop.push({
        playerId: getSeatIdFromPosition('BB', inputModeSettings.maxPlayers),
        type: 'post', 
        amount: inputModeSettings.bigBlind
      });
    } else {
      // Heads up: button is small blind
      currentPot = inputModeSettings.smallBlind + inputModeSettings.bigBlind;
      streetPots.preflop.start = 0;
      streetPots.preflop.end = currentPot;
      
      streetActions.preflop.push({
        playerId: getSeatIdFromPosition('BTN', inputModeSettings.maxPlayers),
        type: 'post',
        amount: inputModeSettings.smallBlind
      });
      streetActions.preflop.push({
        playerId: getSeatIdFromPosition('BB', inputModeSettings.maxPlayers),
        type: 'post',
        amount: inputModeSettings.bigBlind
      });
    }

    // Process each street's data
    for (const street of streets) {
      const streetData = inputModeStreetData[street];
      if (streetData && streetData.actionHistory && streetData.actionHistory.length > 0) {
        // Use actionHistory which has complete action data including amounts
        const actionsArray = streetData.actionHistory.map(action => ({
          playerId: action.seatId,
          type: action.action.toLowerCase(),
          amount: action.amount || null
        }));
        streetActions[street].push(...actionsArray);

        // Calculate pot for this street
        const streetStartPot = street === 'preflop' ? currentPot : streetPots[streets[streets.indexOf(street) - 1]].end;
        const streetContributions = Object.values(streetData.playerContributions).reduce((sum, contrib) => sum + contrib, 0);
        const streetEndPot = streetStartPot + streetContributions;
        
        streetPots[street].start = streetStartPot;
        streetPots[street].end = streetEndPot;
        currentPot = streetEndPot;
      } else if (street !== 'preflop') {
        // For non-preflop streets with no actions, inherit from previous street
        const prevStreet = streets[streets.indexOf(street) - 1];
        streetPots[street].start = streetPots[prevStreet].end;
        streetPots[street].end = streetPots[prevStreet].end;
      }
    }

    // Calculate hero's total contribution
    const heroSeatId = getSeatIdFromPosition(inputModeSettings.heroPosition, inputModeSettings.maxPlayers);
    let heroTotalContribution = 0;
    
    for (const street of streets) {
      const streetData = inputModeStreetData[street];
      if (streetData && streetData.playerContributions[heroSeatId]) {
        heroTotalContribution += streetData.playerContributions[heroSeatId];
      }
    }

    return {
      metadata: {
        handId: `input-${Date.now()}`,
        timestamp: new Date().toISOString(),
        handType: "Live",
        tableType: "Cash Game",
        stakes: {
          smallBlind: inputModeSettings.smallBlind,
          bigBlind: inputModeSettings.bigBlind
        }
      },
      players: Array.from({length: inputModeSettings.maxPlayers}, (_, i) => ({
        id: i,
        name: i === heroSeatId ? "Hero" : `Player ${i}`,
        isHero: i === heroSeatId,
        position: getPositionLabel(i, inputModeSettings.maxPlayers),
        initialStack: inputModeStackSizes[i] || inputModeSettings.bigBlind * 100
      })),
      heroCards: inputModeHeroCards,
      board: [
        inputModeCommunityCards.flopCard1,
        inputModeCommunityCards.flopCard2,
        inputModeCommunityCards.flopCard3,
        inputModeCommunityCards.turnCard,
        inputModeCommunityCards.riverCard
      ].filter(card => card && card.trim() !== ''),
      preflop: { 
        potSizeAtStart: streetPots.preflop.start, 
        potSizeAtEnd: streetPots.preflop.end, 
        cardsDealt: null, 
        actions: streetActions.preflop 
      },
      flop: { 
        potSizeAtStart: streetPots.flop.start, 
        potSizeAtEnd: streetPots.flop.end, 
        cardsDealt: null, 
        actions: streetActions.flop 
      },
      turn: { 
        potSizeAtStart: streetPots.turn.start, 
        potSizeAtEnd: streetPots.turn.end, 
        cardsDealt: null, 
        actions: streetActions.turn 
      },
      river: { 
        potSizeAtStart: streetPots.river.start, 
        potSizeAtEnd: streetPots.river.end, 
        cardsDealt: null, 
        actions: streetActions.river 
      },
      finalPot: currentPot,
      heroTotalContribution: heroTotalContribution,
      declaredWinnerIds: null,
      isChoppedPot: null
    };
  };

  // Handle analysis button click
  const handleAnalyzeHand = async () => {
    if (!activeHandData) {
      setAnalysisSubmissionStatus("No hand data available");
      return;
    }

    // For input mode, we need to use the input mode data, not the sample replay data
    let handDataToSubmit: HandSummaryFull;
    if (currentMode === 'input') {
      // Create hand summary from current input mode state using the conversion function
      handDataToSubmit = convertInputModeToHandSummary();
      
      console.log('Using input mode data for analysis:', handDataToSubmit);
    } else {
      // Use the loaded replay data
      handDataToSubmit = activeHandData;
      console.log('Using replay mode data for analysis:', handDataToSubmit);
    }

    // Check PlayFab session
    if (!PlayFab.hasValidSession()) {
      setAnalysisSubmissionStatus("Please log in to analyze hands");
      return;
    }

    console.log('PlayFab session valid, player ID:', PlayFab.getPlayerId());

    setIsSubmittingAnalysis(true);
    setAnalysisSubmissionStatus("Submitting hand for analysis...");
    
    try {
      await submitHandForAnalysis(handDataToSubmit);
      setAnalysisSubmissionStatus("Hand submitted successfully! Analysis will be available shortly.");
      
      // Clear status after 3 seconds
      setTimeout(() => {
        setAnalysisSubmissionStatus(null);
      }, 3000);
    } catch (error) {
      setAnalysisSubmissionStatus(`Failed to submit hand: ${error}`);
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setAnalysisSubmissionStatus(null);
      }, 5000);
    } finally {
      setIsSubmittingAnalysis(false);
    }
  };

  // Undo last action function
  const handleUndoLastAction = () => {
    // In replay mode, undo means go back one action (same as previous action)
    if (currentMode === 'replay') {
      handlePrevAction();
      return;
    }
    
    // In input mode, use the existing undo logic
    if (inputModeActionHistory.length === 0) {
      console.log(`[INPUT MODE] No actions to undo`);
      return;
    }

    const lastAction = inputModeActionHistory[inputModeActionHistory.length - 1];
    console.log(`[INPUT MODE] Undoing action:`, lastAction);

    // Restore previous state
    setInputModePlayerContributions(prev => ({
      ...prev,
      [lastAction.seatId]: lastAction.previousContribution
    }));

    // Restore auto-fold status
    setInputModeSettings(prev => {
      const newAutoFoldIds = new Set(prev.autoFoldPlayerIds);
      if (lastAction.previousAutoFoldStatus) {
        newAutoFoldIds.add(lastAction.seatId);
      } else {
        newAutoFoldIds.delete(lastAction.seatId);
      }
      return {
        ...prev,
        autoFoldPlayerIds: newAutoFoldIds
      };
    });

    // Restore folded status
    setInputModeFoldedPlayers(prev => {
      const newFoldedPlayers = new Set(prev);
      if (lastAction.previousFoldedStatus) {
        newFoldedPlayers.add(lastAction.seatId);
      } else {
        newFoldedPlayers.delete(lastAction.seatId);
      }
      return newFoldedPlayers;
    });

    // Restore stack size
    setInputModeStackSizes(prev => ({
      ...prev,
      [lastAction.seatId]: lastAction.previousStack
    }));

    // Remove from history
    setInputModeActionHistory(prev => prev.slice(0, -1));

    console.log(`[INPUT MODE] Action undone for seat ${lastAction.seatId}`);
  };

  const pokerVisualProps = useMemo(() => {
    if (isInputMode) {
      // Input mode props - simplified setup mode
      const inputSeats = Array.from({length: inputModeSettings.maxPlayers}, (_, i) => ({
        id: i,
        label: getPositionLabel(i, inputModeSettings.maxPlayers),
        tag: i === 1 ? 'SB' : i === 2 ? 'BB' : undefined
      }));
      
      const inputStackSizes: Record<number, number> = {};
      const defaultStackSize = inputModeSettings.bigBlind * 100; // 100x BB
      inputSeats.forEach(seat => {
        inputStackSizes[seat.id] = defaultStackSize; // All players have stack sizes
      });

      // Find hero seat ID based on selected position
      const heroSeat = inputSeats.find(seat => seat.label === inputModeSettings.heroPosition);
      const heroSeatId = heroSeat ? heroSeat.id : null;

      // Helper to determine the next player who should act (for visual guidance)
      const calculateNextPlayerToActId = (): number | null => {
        const maxPlayers = inputModeSettings.maxPlayers;

        // Helper: is this seat still active in the hand?
        const isSeatActive = (seatId: number): boolean => {
          return !inputModeFoldedPlayers.has(seatId) && !inputModeSettings.autoFoldPlayerIds.has(seatId);
        };

        // Determine the logical starting seat for action on this street
        let startSeat: number;

        // If someone has already acted this street, start with the seat after the last action
        const lastActionSeatId = inputModeActionHistory.length > 0 ? inputModeActionHistory[inputModeActionHistory.length - 1].seatId : null;
        if (lastActionSeatId !== null) {
          startSeat = (lastActionSeatId + 1) % maxPlayers;
        } else {
          if (inputModeCurrentStreet === 'preflop') {
            if (maxPlayers === 2) {
              // Heads-up: SB/Button acts first pre-flop
              startSeat = getSeatIdFromPosition('SB', maxPlayers);
            } else {
              // Everyone else: seat after the BB acts first pre-flop
              startSeat = (2 + 1) % maxPlayers; // BB is always seat 2 in our seating schema
            }
          } else {
            // Post-flop streets: SB (seat 1) is first to act
            startSeat = getSeatIdFromPosition('SB', maxPlayers);
          }
        }

        // Collect seats that have already acted on this street
        const actedSeatIds = new Set<number>(Object.keys(inputModeActions).map(Number));

        // Scan table starting from startSeat until we find the next active seat who hasn't acted yet
        for (let offset = 0; offset < maxPlayers; offset++) {
          const seatId = (startSeat + offset) % maxPlayers;
          if (!isSeatActive(seatId)) continue;          // Skip inactive seats
          if (actedSeatIds.has(seatId)) continue;       // Skip seats that already acted
          return seatId;                                // Found the next to act
        }

        return null; // Everyone has acted or no active seats
      };

      const nextToActId = calculateNextPlayerToActId();

      return {
        seatCount: inputModeSettings.maxPlayers,
        seats: inputSeats,
        heroSeatId: heroSeatId,
        heroTargetVisualIndex: 0,
        isSetupComplete: true, // Show cards in input mode
        smallBlind: inputModeSettings.smallBlind,
        bigBlind: inputModeSettings.bigBlind,
        heroCards: inputModeHeroCards, // Use state for hero cards
        onHeroCard1Click: inputModeHeroCards.holeCard1 ? handleHeroCard1Click : handleSetBothHeroCards, // Use both cards handler if card1 is empty
        onHeroCard2Click: inputModeHeroCards.holeCard2 ? handleHeroCard2Click : handleSetBothHeroCards, // Use both cards handler if card2 is empty
        communityCards: inputModeCommunityCards, // Use state for community cards
        onFlopCard1Click: handleFlopCard1Click,
        onFlopCard2Click: handleFlopCard2Click,
        onFlopCard3Click: handleFlopCard3Click,
        onTurnCardClick: handleTurnCardClick,
        onRiverCardClick: handleRiverCardClick,
        stackSizes: inputModeStackSizes,
        currentPlayerId: inputModeActivePlayerId, // Use the input mode active player ID
        foldedPlayerIds: inputModeFoldedPlayers, // Use input mode folded players
        currentBetToCall: 0,
        currentStreet: inputModeCurrentStreet, // Use input mode current street
        nextPlayerToActId: nextToActId,
        onSeatClick: (seatId: number) => {
          if (isInputMode) {
            console.log(`[INPUT MODE] Seat ${seatId} clicked. Current active: ${inputModeActivePlayerId}. Auto-fold set:`, Array.from(inputModeSettings.autoFoldPlayerIds));

            // Handle clicking on folded players - different logic for preflop vs post-flop
            if (inputModeFoldedPlayers.has(seatId)) {
              if (inputModeCurrentStreet === 'preflop') {
                // On preflop: allow reactivating folded players by clicking
                console.log(`[INPUT MODE] Preflop: Reactivating folded player ${seatId}`);
                setInputModeFoldedPlayers(prev => {
                  const newFoldedPlayers = new Set(prev);
                  newFoldedPlayers.delete(seatId);
                  return newFoldedPlayers;
                });
                // Continue to open radial menu below
              } else {
                // On post-flop: check if they folded on current street vs previous street
                const currentStreetFoldedPlayers = inputModeStreetData[inputModeCurrentStreet]?.foldedPlayers || new Set();
                const foldedOnCurrentStreet = currentStreetFoldedPlayers.has(seatId);
                
                if (foldedOnCurrentStreet) {
                  // Player folded on current street - allow reactivation for error correction
                  console.log(`[INPUT MODE] Post-flop: Reactivating player ${seatId} who folded on current street (${inputModeCurrentStreet})`);
                  setInputModeFoldedPlayers(prev => {
                    const newFoldedPlayers = new Set(prev);
                    newFoldedPlayers.delete(seatId);
                    return newFoldedPlayers;
                  });
                  // Continue to open radial menu below
                } else {
                  // Player folded on a previous street - cannot reactivate
                  console.log(`[INPUT MODE] Post-flop: Seat ${seatId} folded on previous street - ignoring click (cannot reactivate)`);
                  return;
                }
              }
            }

            // Handle clicking on auto-fold players - different logic for preflop vs post-flop
            if (inputModeSettings.autoFoldPlayerIds.has(seatId)) {
              if (inputModeCurrentStreet === 'preflop') {
                // On preflop: allow activating auto-fold players by clicking
                console.log(`[INPUT MODE] Preflop: Activating auto-fold player ${seatId}`);
                setInputModeSettings(prev => {
                  const newAutoFoldIds = new Set(prev.autoFoldPlayerIds);
                  newAutoFoldIds.delete(seatId);
                  return { ...prev, autoFoldPlayerIds: newAutoFoldIds };
                });
                // Continue to open radial menu below
              } else {
                // On post-flop: auto-fold players stay inactive
                console.log(`[INPUT MODE] Post-flop: Seat ${seatId} is auto-fold (inactive) - ignoring click. Only preflop allows activating inactive players.`);
                return;
              }
            }

            if (inputModeActivePlayerId === seatId) {
              setInputModeActivePlayerId(null); // Close radial menu if clicking the same seat
              console.log(`[INPUT MODE] Radial menu closed for seat ${seatId}.`);
            } else {
              setInputModeActivePlayerId(seatId); // Open/move radial menu to this seat
              console.log(`[INPUT MODE] Radial menu opened for seat ${seatId}.`);
            }

            // Clear any "recent action" indicator on the clicked seat
            if (inputModeActions[seatId]) {
              setInputModeActions(prevActions => {
                const newActions = { ...prevActions };
                delete newActions[seatId];
                return newActions;
              });
            }
          } else {
            // Logic for replay mode seat click (if any)
            console.log("Seat clicked in Replay Mode:", seatId);
          }
        },
        onAction: (seatId: number, action: ActionType) => {
          console.log(`[INPUT MODE] Action selected for seat ${seatId}: ${action}`);
          
          // Record current state for undo functionality
          const currentContribution = inputModePlayerContributions?.[seatId] || 0;
          const currentAutoFoldStatus = inputModeSettings.autoFoldPlayerIds.has(seatId);
          const currentFoldedStatus = inputModeFoldedPlayers.has(seatId);
          const currentStack = inputModeStackSizes[seatId] ?? 0;
          
          // Handle the action based on type with proper betting logic
          if (action === 'fold') {
            // If player folds, add them to folded list (they become invisible)
            setInputModeFoldedPlayers(prev => {
              const newFoldedPlayers = new Set(prev);
              newFoldedPlayers.add(seatId);
              return newFoldedPlayers;
            });
            console.log(`[INPUT MODE] Player ${seatId} folded - removed from table`);
          } else {
            // For other actions (call, check, raise, bet), ensure they're NOT in auto-fold
            setInputModeSettings(prev => {
              const newAutoFoldIds = new Set(prev.autoFoldPlayerIds);
              newAutoFoldIds.delete(seatId); // Remove from auto-fold if they were there
              return {
                ...prev,
                autoFoldPlayerIds: newAutoFoldIds
              };
            });
            
            // Implement betting logic
            if (action === 'call') {
              // Call matches the current highest bet, but cap by player's remaining stack
              const currentHighestBet = Math.max(...Object.values(inputModePlayerContributions || {}), inputModeSettings.bigBlind);
              const playerCurrentBet = inputModePlayerContributions?.[seatId] || 0;
              const stackLeft = inputModeStackSizes[seatId] ?? 0;
              const callNeeded = currentHighestBet - playerCurrentBet;
              const actualCall = Math.min(callNeeded, stackLeft);   // cap by stack
              
              if (actualCall > 0) {
                setInputModePlayerContributions(prev => ({
                  ...prev,
                  [seatId]: playerCurrentBet + actualCall
                }));
                
                // Subtract the actual call amount from player's stack
                setInputModeStackSizes(prev => ({
                  ...prev,
                  [seatId]: stackLeft - actualCall
                }));
                
                console.log(`[INPUT MODE] Player ${seatId} called for $${actualCall} (total contribution: $${playerCurrentBet + actualCall})`);
              }
            } else if (action === 'check') {
              // Check - no additional money, but ensure player is marked as active
              console.log(`[INPUT MODE] Player ${seatId} checked`);
            } else if (action === 'straddle') {
              // Straddle - bet 2x BB, but cap by player's remaining stack
              const straddleAmount = inputModeSettings.bigBlind * 2;
              const playerCurrentBet = inputModePlayerContributions?.[seatId] || 0;
              const stackLeft = inputModeStackSizes[seatId] ?? 0;
              const straddleNeeded = straddleAmount - playerCurrentBet;
              const actualStraddle = Math.min(straddleNeeded, stackLeft);   // cap by stack
              
              if (actualStraddle > 0) {
                setInputModePlayerContributions(prev => ({
                  ...prev,
                  [seatId]: playerCurrentBet + actualStraddle
                }));
                
                // Subtract the actual straddle amount from player's stack
                setInputModeStackSizes(prev => ({
                  ...prev,
                  [seatId]: stackLeft - actualStraddle
                }));
                
                console.log(`[INPUT MODE] Player ${seatId} straddled for $${actualStraddle} (total contribution: $${playerCurrentBet + actualStraddle})`);
              }
            }
            // Note: raise and bet actions are now handled by the bet sizing dialog
            
            console.log(`[INPUT MODE] Player ${seatId} performed action: ${action} - ensured they're active`);
          }
          
          // Add to action history for undo functionality
          setInputModeActionHistory(prev => [...prev, {
            seatId,
            action,
            timestamp: Date.now(),
            previousContribution: currentContribution,
            previousAutoFoldStatus: currentAutoFoldStatus,
            previousFoldedStatus: currentFoldedStatus,
            previousStack: currentStack // Assuming previousStack is 0 for this action
          }]);
          
          // Track the action for visual feedback (except fold) - stays until street ends
          if (action !== 'fold') {
            setInputModeActions(prev => ({
              ...prev,
              [seatId]: { action, timestamp: Date.now() }
            }));
          }
          
          // Close the radial menu after any action
          setInputModeActivePlayerId(null);
        },
        onRaiseAmount: (seatId: number, amount: number) => {
          console.log(`[INPUT MODE] Bet sizing requested for seat ${seatId}`);
          
          // Determine if this is a bet or raise based on current betting situation
          const playerContribution = inputModePlayerContributions?.[seatId] || 0;
          const currentHighestBet = Math.max(...Object.values(inputModePlayerContributions || {}), inputModeSettings.bigBlind);
          const amountToCall = currentHighestBet - playerContribution;
          
          setBetSizingSeatId(seatId);
          setBetSizingAction(amountToCall > 0 ? 'raise' : 'bet');
          setShowBetSizingDialog(true);
        },
        onStackChange: () => {},
        playerStreetContributions: inputModePlayerContributions,
        pot: (() => {
          // Always take the latest contributions for the CURRENT street from live state,
          // and use the stored snapshot for any PREVIOUS streets.
          let totalPot = 0;

          const streetsOrder: Street[] = ['preflop', 'flop', 'turn', 'river'];
          const currentStreetIndex = streetsOrder.indexOf(inputModeCurrentStreet);

          streetsOrder.forEach((street, index) => {
            if (index < currentStreetIndex) {
              // Past street – use stored snapshot
              const data = inputModeStreetData[street];
              if (data) {
                totalPot += Object.values(data.playerContributions || {}).reduce((s, v) => s + v, 0);
              }
            } else if (index === currentStreetIndex) {
              // Current street – use live contributions
              totalPot += Object.values(inputModePlayerContributions).reduce((s, v) => s + v, 0);
            }
            // Future streets ignored
          });

          return totalPot;
        })(),
        lastActionPlayerId: null,
        lastActionType: null,
        lastActionAmount: null,
        isReplayMode: false,
        isInputMode: true,
        autoFoldPlayerIds: inputModeSettings.autoFoldPlayerIds,
        inputModeActions: inputModeActions, // Pass actions for visual feedback
        seatsWithActions: new Set(Object.keys(inputModeActions).map(Number)), // Pass which seats have acted
        onBetClick: handleBetClick, // Add bet click handler
        // Street navigation props for input mode
        inputModeCurrentStreet: inputModeCurrentStreet,
        onStreetChange: handleSwitchToStreet,
        // Pass info about which players folded on current street vs previous streets
        currentStreetFoldedPlayers: inputModeStreetData[inputModeCurrentStreet]?.foldedPlayers || new Set(),
        onUndoLastAction: handleUndoLastAction,
        onAnalyzeHand: handleAnalyzeHand,
      };
    }

    // Replay mode props - existing functionality
    return {
      seatCount: seatCount,
      seats: seats,
      heroSeatId: null,
      heroTargetVisualIndex: 0, 
      isSetupComplete: true, 
      smallBlind: activeHandData.metadata.stakes.smallBlind,
      bigBlind: activeHandData.metadata.stakes.bigBlind,
      heroCards: activeHandData.heroCards,
      communityCards: {
        flopCard1: displayedCommunityCards.length > 0 ? displayedCommunityCards[0] : '',
        flopCard2: displayedCommunityCards.length > 1 ? displayedCommunityCards[1] : '',
        flopCard3: displayedCommunityCards.length > 2 ? displayedCommunityCards[2] : '',
        turnCard: displayedCommunityCards.length > 3 ? displayedCommunityCards[3] : '',
        riverCard: displayedCommunityCards.length > 4 ? displayedCommunityCards[4] : '',
      },
      stackSizes: currentStackSizes,
      currentPlayerId: currentPlayerToActId, 
      foldedPlayerIds: foldedPlayerIds,
      currentBetToCall: currentBet, 
      currentStreet: currentStreetKey,
      nextPlayerToActId: currentStreetData && currentActionIndex < currentStreetData.actions.length - 1 
          ? currentStreetData.actions[currentActionIndex + 1].playerId 
          : (streetsOrder.indexOf(currentStreetKey) < streetsOrder.length -1 && activeHandData[streetsOrder[streetsOrder.indexOf(currentStreetKey)+1]]?.actions.length > 0 ? activeHandData[streetsOrder[streetsOrder.indexOf(currentStreetKey)+1]].actions[0].playerId : null),
      onSeatClick: (seatId: number) => {
        if (isInputMode) {
          console.log(`[INPUT MODE] Seat ${seatId} clicked. Current active: ${inputModeActivePlayerId}. Auto-fold set:`, Array.from(inputModeSettings.autoFoldPlayerIds));

          // Handle clicking on folded players - different logic for preflop vs post-flop
          if (inputModeFoldedPlayers.has(seatId)) {
            if (inputModeCurrentStreet === 'preflop') {
              // On preflop: allow reactivating folded players by clicking
              console.log(`[INPUT MODE] Preflop: Reactivating folded player ${seatId}`);
              setInputModeFoldedPlayers(prev => {
                const newFoldedPlayers = new Set(prev);
                newFoldedPlayers.delete(seatId);
                return newFoldedPlayers;
              });
              // Continue to open radial menu below
            } else {
              // On post-flop: check if they folded on current street vs previous street
              const currentStreetFoldedPlayers = inputModeStreetData[inputModeCurrentStreet]?.foldedPlayers || new Set();
              const foldedOnCurrentStreet = currentStreetFoldedPlayers.has(seatId);
              
              if (foldedOnCurrentStreet) {
                // Player folded on current street - allow reactivation for error correction
                console.log(`[INPUT MODE] Post-flop: Reactivating player ${seatId} who folded on current street (${inputModeCurrentStreet})`);
                setInputModeFoldedPlayers(prev => {
                  const newFoldedPlayers = new Set(prev);
                  newFoldedPlayers.delete(seatId);
                  return newFoldedPlayers;
                });
                // Continue to open radial menu below
              } else {
                // Player folded on a previous street - cannot reactivate
                console.log(`[INPUT MODE] Post-flop: Seat ${seatId} folded on previous street - ignoring click (cannot reactivate)`);
                return;
              }
            }
          }

          // Handle clicking on auto-fold players - different logic for preflop vs post-flop
          if (inputModeSettings.autoFoldPlayerIds.has(seatId)) {
            if (inputModeCurrentStreet === 'preflop') {
              // On preflop: allow activating auto-fold players by clicking
              console.log(`[INPUT MODE] Preflop: Activating auto-fold player ${seatId}`);
              setInputModeSettings(prev => {
                const newAutoFoldIds = new Set(prev.autoFoldPlayerIds);
                newAutoFoldIds.delete(seatId);
                return { ...prev, autoFoldPlayerIds: newAutoFoldIds };
              });
              // Continue to open radial menu below
            } else {
              // On post-flop: auto-fold players stay inactive
              console.log(`[INPUT MODE] Post-flop: Seat ${seatId} is auto-fold (inactive) - ignoring click. Only preflop allows activating inactive players.`);
              return;
            }
          }

          if (inputModeActivePlayerId === seatId) {
            setInputModeActivePlayerId(null); // Close radial menu if clicking the same seat
            console.log(`[INPUT MODE] Radial menu closed for seat ${seatId}.`);
          } else {
            setInputModeActivePlayerId(seatId); // Open/move radial menu to this seat
            console.log(`[INPUT MODE] Radial menu opened for seat ${seatId}.`);
          }

          // Clear any "recent action" indicator on the clicked seat
          if (inputModeActions[seatId]) {
            setInputModeActions(prevActions => {
              const newActions = { ...prevActions };
              delete newActions[seatId];
              return newActions;
            });
          }
        } else {
          // Logic for replay mode seat click (if any)
          console.log("Seat clicked in Replay Mode:", seatId);
        }
      }, 
      onAction: (seatId: number, action: ActionType) => console.log("[INPUT MODE] Action selected for seat " + seatId + ": " + action), // Keep this for action logging
      onStackChange: handleStackChange, 
      playerStreetContributions: playerStreetContributions,
      pot: currentPotSize,
      lastActionPlayerId: lastActionPlayerId,
      lastActionType: lastActionType,
      lastActionAmount: lastActionAmount,
      isReplayMode: true,
      isInputMode: false,
      // Street navigation props - not used in replay mode
      inputModeCurrentStreet: undefined,
      onStreetChange: undefined,
      currentStreetFoldedPlayers: new Set<number>(), // Empty set for replay mode
    };
  }, [
    isInputMode, inputModeSettings, inputModeActivePlayerId, inputModePlayerContributions, inputModeFoldedPlayers, inputModeHeroCards, inputModeCommunityCards, inputModeStackSizes, inputModeStreetData, // Added inputModeStreetData
    seatCount, seats, activeHandData, displayedCommunityCards, 
    currentStackSizes, currentPlayerToActId, foldedPlayerIds, currentBet, currentStreetKey, 
    currentStreetData, currentActionIndex, streetsOrder, playerStreetContributions, currentPotSize,
    lastActionPlayerId, lastActionType, lastActionAmount, inputModeActions,
    showBetSizingDialog, betSizingAction, betSizingSeatId, inputModeActionHistory, inputModeCurrentStreet,
    handleBetClick
  ]);

  const StructuredAnalysisDisplay: React.FC<{ analysis: any, currentStreet: Street }> = ({ analysis, currentStreet }) => {
    if (!analysis) return null;
    const overallSummary = analysis.summary;
    const streetAnalysisData = analysis.streets?.[currentStreet];
    return (
      <div style={{ marginTop: '20px', border: '1px solid #ddd', padding: '15px', borderRadius: '8px', backgroundColor: '#fff8e1', color: '#555' }}>
        <h3 style={{ marginTop: '0', borderBottom: '1px solid #eee', paddingBottom: '10px', color: '#333' }}>Hand Analysis Details:</h3>
        {overallSummary && (
          <div style={{ marginBottom: '15px' }}>
            <h4 style={{color: '#444'}}>Overall Summary:</h4>
            {overallSummary.overallLineAssessment && <p><strong>Assessment:</strong> {overallSummary.overallLineAssessment}</p>}
            {overallSummary.overallRating !== undefined && <p><strong>Rating:</strong> {overallSummary.overallRating}/10</p>}
            {overallSummary.heroProfileTag && <p><strong>Hero Profile:</strong> {overallSummary.heroProfileTag}</p>}
            {overallSummary.improvementFocus && overallSummary.improvementFocus.length > 0 && (
              <>
                <p><strong>Improvement Focus:</strong></p>
                <ul style={{paddingLeft: '20px'}}>
                  {overallSummary.improvementFocus.map((item: string, index: number) => <li key={index}>{item}</li>)}
                </ul>
              </>
            )}
          </div>
        )}
        {streetAnalysisData && (
          <div style={{ borderTop: '1px dashed #ccc', paddingTop: '15px' }}>
            <h4 style={{color: '#444'}}>{currentStreet.charAt(0).toUpperCase() + currentStreet.slice(1)} Analysis:</h4>
            {streetAnalysisData.rating !== undefined && <p><strong>Rating:</strong> {streetAnalysisData.rating}/10</p>}
            {streetAnalysisData.analysis && <p><strong>Analysis:</strong> {streetAnalysisData.analysis} (This is also shown as 'Coaching Tip')</p>}
            {streetAnalysisData.learningTips && streetAnalysisData.learningTips.length > 0 && (
              <>
                <p><strong>Learning Tips:</strong></p>
                <ul style={{paddingLeft: '20px'}}>
                  {streetAnalysisData.learningTips.map((tip: string, index: number) => <li key={index}>{tip}</li>)}
                </ul>
              </>
            )}
          </div>
        )}
        {!overallSummary && !streetAnalysisData && <p>No specific analysis details found for the current view in the loaded JSON.</p>}
      </div>
    );
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 15px',
    fontSize: '14px',
    margin: '5px',
    cursor: 'pointer',
    backgroundColor: '#61dafb',
    color: '#282c34',
    border: 'none',
    borderRadius: '4px',
    fontWeight: 'bold'
  };


  // Handlers for bet sizing dialog
  const handleBetAmountConfirm = (amount: number) => {
    if (betSizingSeatId !== null) {
      console.log(`[INPUT MODE] Bet amount confirmed: ${amount} for seat ${betSizingSeatId}`);
      
      // Record current state for undo functionality
      const currentContribution = inputModePlayerContributions?.[betSizingSeatId] || 0;
      const currentAutoFoldStatus = inputModeSettings.autoFoldPlayerIds.has(betSizingSeatId);
      const currentFoldedStatus = inputModeFoldedPlayers.has(betSizingSeatId);
      const currentStack = inputModeStackSizes[betSizingSeatId] ?? 0;
      
      // Cap bet amount by player's remaining stack
      const stackLeft = inputModeStackSizes[betSizingSeatId] ?? 0;
      const { finalAmount: finalBetAmount, additionalAmount: actualAdditional } = capBetAmount(amount, currentContribution, stackLeft);
      
      // Update player contributions with the capped bet amount
      setInputModePlayerContributions(prev => ({
        ...prev,
        [betSizingSeatId]: finalBetAmount
      }));
      
      // Subtract the actual additional amount from player's stack
      setInputModeStackSizes(prev => ({
        ...prev,
        [betSizingSeatId]: stackLeft - actualAdditional
      }));
      
      // Track the action for feedback
      setInputModeActions(prev => ({
        ...prev,
        [betSizingSeatId]: { action: betSizingAction, timestamp: Date.now() }
      }));
      
      // Ensure player is not in auto-fold
      setInputModeSettings(prev => {
        const newAutoFoldIds = new Set(prev.autoFoldPlayerIds);
        newAutoFoldIds.delete(betSizingSeatId);
        return {
          ...prev,
          autoFoldPlayerIds: newAutoFoldIds
        };
      });
      
      // Add to action history for undo functionality
      setInputModeActionHistory(prev => [...prev, {
        seatId: betSizingSeatId,
        action: betSizingAction,
        amount: finalBetAmount,
        timestamp: Date.now(),
        previousContribution: currentContribution,
        previousAutoFoldStatus: currentAutoFoldStatus,
        previousFoldedStatus: currentFoldedStatus,
        previousStack: currentStack // Assuming previousStack is 0 for this action
      }]);
      
      console.log(`[INPUT MODE] Player ${betSizingSeatId} ${betSizingAction} to $${finalBetAmount}`);
      
      // REMOVED: Visual action feedback is not needed
      /*
      // Clear the action feedback after 3 seconds
      setTimeout(() => {
        setInputModeActions(prev => {
          const newActions = { ...prev };
          delete newActions[betSizingSeatId];
          return newActions;
        });
      }, 3000);
      */
    }
    
    // Close dialog and clear state
    setShowBetSizingDialog(false);
    setBetSizingSeatId(null);
    setInputModeActivePlayerId(null); // Close radial menu
  };

  const handleBetAmountCancel = () => {
    console.log(`[INPUT MODE] Bet sizing cancelled`);
    setShowBetSizingDialog(false);
    setBetSizingSeatId(null);
    // Don't close radial menu - let user select a different action
  };


  // Function to advance to next street in input mode
  const handleNextStreet = () => {
    const streetOrder: Street[] = ['preflop', 'flop', 'turn', 'river'];
    const currentIndex = streetOrder.indexOf(inputModeCurrentStreet);

    if (currentIndex < streetOrder.length - 1) {
      const nextStreet = streetOrder[currentIndex + 1];

      console.log(`[INPUT MODE] Advancing from ${inputModeCurrentStreet} to ${nextStreet}`);
      console.log(`[INPUT MODE] Current auto-fold players:`, Array.from(inputModeSettings.autoFoldPlayerIds));
      console.log(`[INPUT MODE] Current folded players:`, Array.from(inputModeFoldedPlayers));

      // Save current street data first
      saveCurrentStreetData();

      // Update the street
      setInputModeCurrentStreet(nextStreet);
      
      // Load the next street's data (if it exists) or create fresh state
      const nextStreetData = inputModeStreetData[nextStreet];
      
      if (nextStreetData) {
        // Load existing data for this street
        console.log(`[INPUT MODE] Loading existing data for ${nextStreet}`);
        setInputModePlayerContributions(nextStreetData.playerContributions);
        setInputModeFoldedPlayers(nextStreetData.foldedPlayers);
        setInputModeSettings(prev => ({
          ...prev,
          autoFoldPlayerIds: nextStreetData.autoFoldPlayerIds
        }));
        setInputModeActions(nextStreetData.actions);
        setInputModeActionHistory(nextStreetData.actionHistory);
      } else {
        // Create fresh state for new street, carrying forward active/inactive status
        console.log(`[INPUT MODE] Creating fresh state for ${nextStreet}, carrying forward player status`);
        
        // Clear action indicators and history for new street
        setInputModeActions({}); 
        setInputModeActionHistory([]); 
        
        // Players who were folded or auto-fold should remain so on the next street
        // This preserves the "once folded, stays folded" and "inactive players stay inactive" behavior
        const playersToHideNextStreet = new Set([
          ...Array.from(inputModeFoldedPlayers), // Keep existing folded players
          ...Array.from(inputModeSettings.autoFoldPlayerIds) // Add ONLY those still in auto-fold (inactive players)
        ]);
        setInputModeFoldedPlayers(playersToHideNextStreet);

        // Reset all contributions for new street (post-flop streets start with 0 contributions)
        const newContributions: Record<number, number> = {};
        for (let i = 0; i < inputModeSettings.maxPlayers; i++) {
          newContributions[i] = 0;
        }
        setInputModePlayerContributions(newContributions);

        // Keep the auto-fold status exactly as it is now
        // Players who took actions are already removed from auto-fold by the onAction handler
        // Players who didn't take actions remain in auto-fold and get moved to folded above
      }

      console.log(`[INPUT MODE] Successfully advanced to ${nextStreet}`);
    } else {
      console.log(`[INPUT MODE] Already on final street (river)`);
    }
  };

  // --- RENDER DRAWER CONTENT ---
  const renderDrawerContent = () => {
    switch (activeTabKey) {
      case "coaching":
        return (
          <div className="coaching-tab-container">
            <CoachingBubble 
              key={`coach-drawer-${currentStreetKey}`}
              text={(loadedAnalysis?.streets?.[currentStreetKey]?.learningTips || []).slice(0,3).join(' • ')} 
              street={currentStreetKey} 
              isVisible={true} 
              streetAnalysis={loadedAnalysis?.streets?.[currentStreetKey]}
              keyMistakes={null}
              isInDrawer={true}
            />
            {!activeCoachingText && (
              <div className="no-coaching-message">
                <p style={{ textAlign: 'center', color: '#9ca3af', fontStyle: 'italic' }}>
                  No coaching available for this action. Coaching will appear automatically when you reach a hero action with analysis.
                </p>
              </div>
            )}
          </div>
        );
      case "controls":
        if (isInputMode) {
          return (
            <div className="input-mode-container">
              <div className="input-mode-header">
                <h3>Hand Setup Mode</h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button 
                    onClick={handleUndoLastAction} 
                    className="control-button control-button-secondary"
                    disabled={inputModeActionHistory.length === 0}
                    title={inputModeActionHistory.length > 0 ? `Undo: ${inputModeActionHistory[inputModeActionHistory.length - 1]?.action}` : 'No actions to undo'}
                  >
                    ↶ Undo
                  </button>
                  <button onClick={handleToggleInputMode} className="control-button control-button-secondary">
                    Exit Setup
                  </button>
                </div>
              </div>
              
              <div className="input-mode-settings">
                <div className="blinds-setup">
                  <label>
                    Small Blind:
                    <input 
                      type="number" 
                      value={inputModeSettings.smallBlind}
                      onChange={(e) => handleInputModeSettingsChange('smallBlind', Number(e.target.value))}
                      min="1"
                    />
                  </label>
                  <label>
                    Big Blind:
                    <input 
                      type="number" 
                      value={inputModeSettings.bigBlind}
                      onChange={(e) => handleInputModeSettingsChange('bigBlind', Number(e.target.value))}
                      min="1"
                    />
                  </label>
                </div>
                
                <div className="players-setup">
                  <label>
                    Max Players:
                    <select 
                      value={inputModeSettings.maxPlayers}
                      onChange={(e) => handleInputModeSettingsChange('maxPlayers', Number(e.target.value))}
                    >
                      {[2,3,4,5,6,7,8,9,10].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Hero Position:
                    <select 
                      value={inputModeSettings.heroPosition}
                      onChange={(e) => handleInputModeSettingsChange('heroPosition', e.target.value as PlaygroundPosition)}
                    >
                      {Array.from({length: inputModeSettings.maxPlayers}, (_, i) => {
                        const position = getPositionLabel(i, inputModeSettings.maxPlayers);
                        return (
                          <option key={position} value={position}>{position}</option>
                        );
                      })}
                    </select>
                  </label>
                </div>
                
                <div className="undo-section" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <button 
                    onClick={handleUndoLastAction} 
                    className="control-button control-button-secondary"
                    disabled={inputModeActionHistory.length === 0}
                    title={inputModeActionHistory.length > 0 ? `Undo: ${inputModeActionHistory[inputModeActionHistory.length - 1]?.action}` : 'No actions to undo'}
                    style={{ width: '100%' }}
                  >
                    ↶ Undo Last Action
                    {inputModeActionHistory.length > 0 && (
                      <span style={{ fontSize: '0.8em', opacity: 0.7, marginLeft: '0.5rem' }}>
                        ({inputModeActionHistory[inputModeActionHistory.length - 1]?.action})
                      </span>
                    )}
                  </button>
                </div>
              </div>
              
              <div className="input-mode-instructions">
                <p><strong>Instructions:</strong></p>
                <ul>
                  <li>Select hero position from the dropdown above</li>
                  <li><strong>Click either hero card slot to set both cards in sequence</strong></li>
                  <li><strong>Click community card slots (flop, turn, river) to set board cards</strong></li>
                  <li>Click seats to show radial menu and select actions</li>
                  <li><strong>Preflop: Folded and auto-fold players can be reactivated by clicking</strong></li>
                  <li><strong>Post-flop: Folded players stay folded permanently, auto-fold players are hidden</strong></li>
                  <li><strong>Click on bet amounts (yellow text) to remove bets and reactivate seats</strong></li>
                  <li>Use the "Undo" button to cancel the last action</li>
                  <li><strong>Use street navigation buttons above table to switch between streets</strong></li>
                  <li><strong>Street data is preserved when switching - go back to fix earlier actions</strong></li>
                  <li><strong>Players who act on one street carry forward as active to next streets</strong></li>
                  <li>Only SB and BB start active by default on preflop</li>
                  <li><strong>Current Street: {inputModeCurrentStreet.toUpperCase()}</strong></li>
                  <li><strong>Hero position: {inputModeSettings.heroPosition}</strong></li>
                  <li>Auto-fold players: {inputModeSettings.autoFoldPlayerIds.size}</li>
                  <li>Active players: {inputModeSettings.maxPlayers - inputModeSettings.autoFoldPlayerIds.size - inputModeFoldedPlayers.size}</li>
                  <li>Folded players: {inputModeFoldedPlayers.size}</li>
                  <li>Actions taken: {inputModeActionHistory.length}</li>
                  <li>Auto-fold seat IDs: {Array.from(inputModeSettings.autoFoldPlayerIds).join(', ')}</li>
                </ul>
              </div>
            </div>
          );
        }
        
        return (
          <div className="controls-container">
            <div className="progress-section">
              <div className="progress-text">
                {currentStreetKey.charAt(0).toUpperCase() + currentStreetKey.slice(1)} - 
                Action {Math.max(0, currentActionIndex + 1)} of {currentStreetData?.actions.length || 0}
                {isAutoPlaying && !playToNextCoaching && <span style={{ color: '#10b981', marginLeft: '10px' }}>▶️ Playing...</span>}
                {playToNextCoaching && <span style={{ color: '#f59e0b', marginLeft: '10px' }}>🎯 Playing to coaching...</span>}
              </div>
              <div className="progress-bar-container">
                <div 
                  className="progress-bar"
                  style={{
                    width: `${(() => {
                      let totalActions = 0;
                      let completedActions = 0;
                      for (const street of streetsOrder) {
                        const streetData = activeHandData[street];
                        if (streetData) {
                          totalActions += streetData.actions.length;
                          const streetIndex = streetsOrder.indexOf(street);
                          const currentStreetIndex = streetsOrder.indexOf(currentStreetKey);
                          if (streetIndex < currentStreetIndex) {
                            completedActions += streetData.actions.length;
                          } else if (streetIndex === currentStreetIndex) {
                            completedActions += Math.max(0, currentActionIndex + 1);
                          }
                        }
                      }
                      return totalActions > 0 ? (completedActions / totalActions) * 100 : 0;
                    })()}%`
                  }}
                />
              </div>
            </div>

            <div className="nav-controls">
              <button onClick={handlePrevAction} className="control-button control-button-nav">⏮️ Prev</button>
              <button onClick={handleNextAction} className="control-button control-button-nav">Next ⏭️</button>
            </div>

            <div className="playback-controls">
              <button onClick={handlePlayPause} className={`control-button ${isAutoPlaying ? 'control-button-pause' : 'control-button-play'}`}>
                {isAutoPlaying ? '⏸️ Pause' : '▶️ Play'}
              </button>
              <button onClick={handleResetToStart} className="control-button control-button-secondary">⏮️ Reset</button>
              <button onClick={handleToggleInputMode} className="control-button control-button-secondary">⚙️ Setup</button>
            </div>

            <div className="coaching-controls">
              <button onClick={handlePlayToNextCoaching} className={`control-button ${playToNextCoaching ? 'control-button-coaching-active' : 'control-button-coaching'}`} disabled={!loadedAnalysis}>
                🎯 Coaching
              </button>
              <select value={autoPlaySpeed} onChange={(e) => handleSpeedChange(Number(e.target.value))} className="control-button" style={{ padding: '8px 12px', backgroundColor: '#374151', color: '#abb2bf', border: '1px solid #4b5563', borderRadius: '6px', minHeight: '40px' }}>
                <option value={3000}>Slow</option>
                <option value={1500}>Normal</option>
                <option value={800}>Fast</option>
                <option value={400}>Very Fast</option>
              </select>
            </div>
          </div>
        );
      case "timeline":
        return (
          activeHandData && <ReplayerActionTimeline
            handData={activeHandData}
            currentStreetKey={currentStreetKey}
            currentActionIndex={currentActionIndex}
            allPlayersInHand={allPlayersInHand}
          />
        );
      case "loadCustom":
        return (
          <div className="load-custom-container">
            <h4 className="load-custom-title">Paste Hand JSON:</h4>
            <textarea 
              value={pastedJsonString}
              onChange={(e) => setPastedJsonString(e.target.value)}
              placeholder='Paste your hand history JSON here...'
              className="custom-json-textarea"
            />
            {jsonParseError && <p className="json-error-text">Error: {jsonParseError}</p>}
            <button onClick={handleParseAndLoadCustomJson} className="control-button load-json-button">
              Parse and Load Hand
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  // --- RENDER COMPONENT ---
  return (
    <div className="hr-page-container">
      {/* Poker Visual Component */}
      {heroSeatId !== null && (
        <PokerVisual {...pokerVisualProps} />
      )}

      {/* Bottom Bar positioned below poker visual */}
      <div className="fixed-bottom-bar">
        <div className="mode-indicator">
          <span className="mode-text">{currentMode.toUpperCase()}</span>
        </div>
        
        <div className="bottom-bar-content">
          {currentMode === 'replay' && (
            <>
              <button className="bottom-btn" title="Setup New Hand" onClick={() => handleModeSwitch('setup')}>
                ⚙️ Setup
              </button>
              <button className="bottom-btn" title="Undo Last Action" onClick={handleUndoLastAction}>
                ↶ Undo
              </button>
              <button className="bottom-btn" title="Previous Action" onClick={handlePrevAction}>⏮️</button>
              <button className="bottom-btn" title="Play / Pause" onClick={handlePlayPause}>
                {isAutoPlaying ? '⏸️' : '▶️'}
              </button>
              <button className="bottom-btn" title="Next Action" onClick={handleNextAction}>⏭️</button>
              <button className="bottom-btn" title="Coaching" onClick={() => { setActiveTabKey('coaching'); setToolbarDialogType('coaching'); }}>
                🎯 Coach
              </button>
              <button className="bottom-btn" title="Load Hand" onClick={() => { setActiveTabKey('loadCustom'); setToolbarDialogType('loadCustom'); }}>
                📜 Load
              </button>
            </>
          )}
          
          {currentMode === 'input' && (
            <>
              <button className="bottom-btn secondary" title="Back to Setup" onClick={() => handleModeSwitch('setup')}>
                ← Setup
              </button>
              <button 
                className="bottom-btn secondary" 
                title={inputModeActionHistory.length > 0 ? `Undo: ${inputModeActionHistory[inputModeActionHistory.length - 1]?.action}` : 'No actions to undo'}
                onClick={handleUndoLastAction}
                disabled={inputModeActionHistory.length === 0}
              >
                ↶ Undo
              </button>
              <div className="street-indicator">
                <span>{inputModeCurrentStreet.toUpperCase()}</span>
              </div>
              <button className="bottom-btn" title="Set Cards" onClick={() => { setActiveTabKey('coaching'); setToolbarDialogType('coaching'); }}>
                🃏 Cards
              </button>
              <button className="bottom-btn primary" title="Submit Hand" onClick={handleSubmitHand}>
                ✓ Submit
              </button>
            </>
          )}
          
          {currentMode === 'submit' && (
            <>
              <button className="bottom-btn secondary" title="Back to Input" onClick={() => handleModeSwitch('input')}>
                ← Edit
              </button>
              <div className="submit-info">
                <span>Review & Submit</span>
              </div>
              <button className="bottom-btn primary" title="Confirm Submit" onClick={handleSubmitHand}>
                ✓ Confirm
              </button>
            </>
          )}
        </div>
      </div>

      {/* Analysis Status Display */}
      {analysisSubmissionStatus && (
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: isSubmittingAnalysis ? '#374151' : analysisSubmissionStatus.includes('Failed') ? '#ef4444' : '#10b981',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '6px',
            zIndex: 1000,
            maxWidth: '300px',
            fontSize: '14px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
        >
          {analysisSubmissionStatus}
        </div>
      )}

      {/* Dialog for toolbar content */}
      <Dialog.Root open={toolbarDialogType !== null} onOpenChange={(open)=>{ if(!open) setToolbarDialogType(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="dialog-overlay" />
          <Dialog.Content className="dialog-content" style={{maxWidth:'600px', width:'90vw', maxHeight:'80vh'}}>
            <div style={{overflowY:'auto', maxHeight:'100%'}}>
              {renderDrawerContent()}
            </div>
            <Dialog.Close asChild>
              <button className="dialog-close">✖</button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Bet Amount Input Dialog */}
      {showBetSizingDialog && betSizingSeatId !== null && (
        <BetAmountInput
          show={showBetSizingDialog}
          onConfirm={handleBetAmountConfirm}
          onCancel={handleBetAmountCancel}
          minBet={(() => {
            if (!isInputMode) return inputModeSettings.bigBlind;
            
            const playerContribution = inputModePlayerContributions?.[betSizingSeatId] || 0;
            const currentHighestBet = Math.max(...Object.values(inputModePlayerContributions || {}), inputModeSettings.bigBlind);
            const amountToCall = currentHighestBet - playerContribution;
            
            if (betSizingAction === 'raise') {
              // For raise: must call + minimum raise (one big blind)
              return currentHighestBet + inputModeSettings.bigBlind;
            } else {
              // For bet: minimum is one big blind
              return inputModeSettings.bigBlind;
            }
          })()}
          maxBet={inputModeSettings.bigBlind * 100} // Default to 100x BB stack
          actionType={betSizingAction}
          currentBetToCall={(() => {
            if (!isInputMode || betSizingSeatId === null) return 0;
            
            const playerContribution = inputModePlayerContributions?.[betSizingSeatId] || 0;
            const currentHighestBet = Math.max(...Object.values(inputModePlayerContributions || {}), inputModeSettings.bigBlind);
            return currentHighestBet - playerContribution;
          })()}
        />
      )}

      {/* Setup Popup */}
      <SetupPopup
        isVisible={showSetupPopup}
        onConfirm={handleSetupConfirm}
        onCancel={handleSetupCancel}
        initialSettings={inputModeSettings}
      />

      {/* Card Picker Dialog for Input Mode */}
      {showCardPicker && isInputMode && (
        <CardPickerDialog 
          show={showCardPicker} // Add the show prop
          cardPosition={cardPickerPosition}
          currentCard={cardPickerPosition === 'card1' ? inputModeHeroCards.holeCard1 : inputModeHeroCards.holeCard2}
          onCardSelect={handleCardSelect}
          onCancel={handleCardPickerCancel}
          getUsedCards={getUsedCards}
        />
      )}
    </div> 
  );
};

export default HandReplayerPage; 